import { Component, DestroyRef, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Subject, debounceTime, distinctUntilChanged, filter, takeUntil, map } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { MappingStatusService } from '../../../services/mbrs/mapping-status.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { CalculationService } from '../../../services/mbrs/calculation.service';
import {
  EqualityValidationService,
  EqualityValidationError,
} from '../../../services/mbrs/equality-validation.service';
import { ValidationContextService } from '../../../services/mbrs/validation-context.service';
import { FormSchemaService } from '../../../services/mbrs/form-schema.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import {
  CROSS_SHEET_VALIDATION_DEBOUNCE_MS,
  FORM_CALCULATION_DEBOUNCE_MS,
  STATE_INHERITANCE_DEBOUNCE_MS,
  STATE_INHERITANCE_FAST_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildEditModeLivePipeline } from '../../../utils/form-edit-pipeline.util';
import { pickSubset } from '../../../utils/pick-subset.util';
import { adoptIntoInlineEditSheet } from '../../../utils/adopt-remote-sheet-state.util';
import { isRowVisible, isCollapsibleHeader as isCollapsibleHeaderRule, headerLevelClass } from '../../../utils/section-visibility.util';
import { SubmitForValidationComponent } from '../../common/submit-for-validation/submit-for-validation.component';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { SkeletonModule } from 'primeng/skeleton';
import { pickRole12SchemaFile } from '../../../services/mbrs/validators/role12-statement-of-profit-or-loss.rules';
import {
  showConsolidated as showConsolidatedCells,
  hasPreviousYear as hasPreviousYearCells,
  resolvePrerequisiteValue,
} from '../../../services/mbrs/validators/dimension-visibility.util';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  referencedSheetKeysFromSchema,
  recomputeReferencedSheets,
  buildSocieColumnContext,
} from '../../../services/mbrs/cross-sheet-recompute.util';
import { ExtractedSumWarningService, EXTRACTED_SUM_MARKER } from '../../../services/mbrs/extracted-sum-warning.service';
import { SummableControl } from '../../../services/mbrs/extracted-sum-warning';
import { ExtractedSumBaselineDebugService } from '../../../services/common/extracted-sum-baseline-debug.service';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import { FieldLinkLabelComponent } from '../../common/field-link-label/field-link-label.component';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_SHORT_MS,
} from '../../../utils/toast-durations.util';
import { computedControlPredicate, revealValidationErrors, enableDualColumnRowAndRevealVisible } from '../../../utils/validation-reveal.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';

interface FieldControl {
  qname: string;
  label: string;
  type: string;
  required?: boolean; // Schema-defined required field for mandatory validation
  requiredIf?: {
    sheet: string;
    field: string;
    equals: string;
    errorMessage: string;
  };
  ui: {
    controlType: string;
    currency?: string;
    placeholder?: string;
    dualColumn?: boolean;
    calculated?: boolean;
    readonly?: boolean;
    calculation?: string;
    level?: number;
    note?: string;
    decimalPlaces?: number;
    linkTo?: {
      role: string;
      qname: string;
    };
  };
}

interface FormSchema {
  role: string;
  sheetId: string;
  label: string;
  description: string;
  controls: FieldControl[];
}

@Component({
  selector: 'app-role12-310000-statement-of-profit-or-loss',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    FieldLinkLabelComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputNumber,
    ButtonModule,
    Toast,
    SubmitForValidationComponent,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    SkeletonModule,
    FieldLinkTargetDirective,
  ],
  providers: [MessageService],
  templateUrl: './role12-310000-statement-of-profit-or-loss.component.html',
  styleUrls: ['./role12-310000-statement-of-profit-or-loss.component.css'],
})
export class Role12310000StatementOfProfitOrLossComponent
  implements OnInit, OnDestroy
{
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private stateService = inject(FilingFormStateService);
  private mappingStatus = inject(MappingStatusService);
  private crossRefService = inject(CrossReferenceService);
  private equalityValidation = inject(EqualityValidationService);
  private validationContext = inject(ValidationContextService);
  private formSchemaService = inject(FormSchemaService);
  private calculationService = inject(CalculationService);
  private extractedSumWarning = inject(ExtractedSumWarningService);
  private baselineDebug = inject(ExtractedSumBaselineDebugService);
  private envService = inject(EnvironmentService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  statementOfProfitOrLossForm!: FormGroup;
  schema: FormSchema | null = null;
  isLoading = true; // Start as true so <app-sheet-loader> shows immediately
  isConsolidated = false;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Statement of Profit or Loss (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.statementOfProfitOrLoss
    );
  }

  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  /**
   * The four (dimension × period) visibility cells for this sheet, derived by
   * the shared, config-driven resolver via CrossReferenceService.getVisibility().
   * The SAME resolver + central registry feeds the pure
   * role12StatementOfProfitOrLossValidator, so the mounted editor and the
   * headless listing cannot drift on which columns are shown/validated.
   */
  get dimensionVisibility() {
    return this.crossRefService.getVisibility(SHEET_KEYS.statementOfProfitOrLoss);
  }

  /**
   * Show the Consolidated columns. Previously set imperatively in
   * checkConsolidatedStatus() from filingInfo's NatureOfFinancialStatements;
   * now a live getter off the shared resolver (prereq-first, then filingInfo).
   * The util fn is imported aliased (`showConsolidatedCells`) to avoid a name
   * clash with this getter.
   */
  get showConsolidatedColumns(): boolean {
    return showConsolidatedCells(this.dimensionVisibility);
  }

  /**
   * Show the PreviousYear columns. Previously set imperatively in
   * checkConsolidatedStatus() from filingInfo's preparation type; now a live
   * getter off the shared resolver. The util fn is imported aliased
   * (`hasPreviousYearCells`) to avoid a name clash with this getter.
   */
  get hasPreviousYear(): boolean {
    return hasPreviousYearCells(this.dimensionVisibility);
  }

  expandedSections = new Set<string>(); // Track which sections are expanded

  /** Bound as a property so the directive gets a stable, `this`-correct function. */
  protected readonly isSectionHeaderFn = (control: any): boolean =>
    this.isSectionHeader(control);
  private editingRows = new Map<string, { [key: string]: number | null }>(); // Track rows in edit mode with original values

  /** Row Mapping-Status frozen at edit-enter; while a row is mid-edit its badge
   *  shows this committed value (not the live in-edit recompute) so the pencil
   *  alone never flips it to 'Edited'. Cleared on Tick/Cancel → badge recomputes. */
  private frozenRowStatus = new Map<string, 'Failed' | 'Edited' | 'Validated*' | 'Validated'>();

  private validationErrors = new Map<string, string[]>();
  private equalityValidationErrors = new Map<
    string,
    EqualityValidationError[]
  >(); // Track equality validation errors
  private mandatoryFieldErrors = new Set<string>(); // Track qnames with mandatory field errors (for red/Failed status)
  /** Qnames whose validationErrors entries were written by runEqualityValidations.
   *  validateSameSheet skips these keys so frozen cross-sheet errors survive a live recompute. */
  private crossSheetErrorQnames = new Set<string>();
  private isLoadingState = false;
  private isCalculating = false;
  private calculatedTotalKeys: string[] = [];
  private isCancellingChanges = false; // Flag to skip validation during cancel flow
  private isStateSelfUpdate = false; // Guards against setupStateListener re-validating on self-triggered state updates
  /**
   * Set once this sheet's initial load has settled. Past that point
   * `setupStateListener` stops re-patching the form — see its docblock for why
   * a post-init repaint silently reverts a row the user has open. Mirrors
   * role23/role24, which carry the identical listener and the identical guard.
   */
  private isInitialLoadComplete = false;
  isValidationComplete = false; // Prevents "Validated -> Failed" flash on page load

  /**
   * Get currency symbol with unit from Role-1
   * e.g., "RM'000", "$'000", "€'000"
   */
  get currencyUnit(): string {
    const symbol = this.crossRefService.getCurrencySymbol();
    return `${symbol}'000`;
  }

  /**
   * Get formatted date range for current year
   * e.g., "01/01/2024 - 31/12/2024"
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getFormattedFinancialYear('current');
  }

  /**
   * Get formatted date range for previous year
   * e.g., "01/01/2023 - 31/12/2023"
   */
  get periodHeaderPrevious(): string {
    return this.crossRefService.getFormattedFinancialYear('previous');
  }

  ngOnInit(): void {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '12',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    this.baselineDebug.bindGlobalShortcut(this.destroyRef);
    this.checkConsolidatedStatus();
    this.loadSchema();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize all sections as expanded by default
   */
  private initializeExpandedSections(): void {
    // Seed EVERY section header (all levels, incl. the level-1 banner) as
    // expanded. Level 1 is seeded — even though it has no toggle — because the
    // shared `isRowVisible` helper consults level-1 ancestors; without it,
    // level-1 would read as collapsed and hide every row under it on load.
    // Seeding by `isSectionHeader` (not a level allow-list) keeps this from
    // drifting as the schema gains levels.
    if (this.schema) {
      this.schema.controls.forEach((control) => {
        if (this.isSectionHeader(control)) {
          this.expandedSections.add(control.qname);
        }
      });
    }
  }

  /**
   * Toggle section expansion
   */
  toggleSection(qname: string): void {
    if (this.expandedSections.has(qname)) {
      this.expandedSections.delete(qname);
    } else {
      this.expandedSections.add(qname);
    }
  }

  /**
   * Check if section is expanded
   */
  isSectionExpanded(qname: string): boolean {
    return this.expandedSections.has(qname);
  }

  /**
   * Check if a row should be shown based on parent section expansion.
   *
   * Delegates to the shared, pure `isRowVisible` helper (monotonic
   * strictly-decreasing ancestor walk), replacing the prior per-level walk that
   * could mis-attribute a deeper preceding header as an ancestor. See
   * `section-visibility.util.ts`. Level-1 roots are seeded as expanded in
   * `initializeExpandedSections`, so they stay visible (they have no toggle).
   */
  shouldShowRow(control: FieldControl, index: number): boolean {
    if (!this.schema) return true;
    return isRowVisible(
      this.schema.controls,
      index,
      (qname) => this.isSectionExpanded(qname),
      (control) => this.isSectionHeader(control)
    );
  }

  /**
   * Check consolidation status from Role-2 (scopeOfFiling). This populates the
   * `isConsolidated` UI flag only.
   *
   * Column visibility (`showConsolidatedColumns` / `hasPreviousYear`) is NO
   * LONGER derived here — it moved to live getters off the shared, config-
   * driven resolver (`CrossReferenceService.getVisibility` →
   * `dimension-visibility.util`), which the pure
   * role12StatementOfProfitOrLossValidator also calls so the mounted editor and
   * the headless listing cannot drift. The resolver reads Nature /
   * preparation-type from filingPrerequisites first, then filingInfo.
   */
  private checkConsolidatedStatus(): void {
    const scopeOfFilingData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);
    this.isConsolidated =
      scopeOfFilingData?.[
        'ssmt-scope:DisclosureOfWhetherFinancialStatementsAreConsolidated'
      ] === 'Yes';
  }

  /**
   * Load form schema from JSON
   * Dynamically loads MFRS or MPERS schema based on accounting standard from Role-0.
   *
   * Schema selection is delegated to `pickRole12SchemaFile()` (in
   * role12-statement-of-profit-or-loss.rules.ts) so that the component's
   * fetch() and the pure validator's static import resolve to the SAME file
   * for the same accounting standard. If the backend ever renames the
   * accounting-standard literal, only the rules file changes — the
   * parity-check harness will catch any consumer that drifted.
   */
  private async loadSchema(): Promise<void> {
    // isLoading already starts as true by default

    // Wait for retrieve-mbrs-data hydration so the accounting standard is
    // populated before schema selection — a mid-hydration refresh mount would
    // otherwise fall back to the MFRS default and MPERS values would never bind
    // (blank form).
    await this.stateService.waitForMbrsHydration('role12');
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

    // Determine which schema to load based on accounting standard. The
    // ternary is replaced by the shared picker for DRY parity with the
    // pure validator's pickRole12Schema() — same input, same selection.
    const isMFRS = this.crossRefService.isMFRS();
    // Pass the RAW accounting standard (not an isMFRS-collapsed value): the
    // picker is MFRS-default and selects MPERS only on the exact MPERS literal,
    // so collapsing an MPERS filing to '' would wrongly load the MFRS schema.
    const accountingStandard =
      this.crossRefService.getCrossReferenceData().accountingStandard;
    const schemaFile = pickRole12SchemaFile(accountingStandard);

    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    fetchSheetSchema<FormSchema>(
        this.loadTraceHandle,
        `assets/forms/${schemaFile}`,
      )
      .then(async (data: FormSchema) => {
        this.schema = data;
        this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);

        // Preload schemas for sheets referenced by equality validation rules
        // so that error messages show field labels instead of raw qnames
        const currentTaxonomy: 'MFRS' | 'MPERS' = isMFRS ? 'MFRS' : 'MPERS';
        await this.validationContext.preloadReferencedSchemas(this.schema, currentTaxonomy);

        this.initializeExpandedSections(); // Initialize expand/collapse state after schema is loaded
        this.buildForm();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
        this.setupEditPipeline(); // Setup reactive calculations + persist pipeline

        // Reveal the sheet's already-computed errors now that the form exists
        // and has been populated. Deterministic replacement for the filing-form
        // container's setTimeout-driven markAllAsTouched, which silently no-ops
        // when a sheet's async init has not yet assigned its FormGroup — see
        // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
        revealValidationErrors(this.statementOfProfitOrLossForm, {
          isComputed: computedControlPredicate(this.schema?.controls),
        });
        this.setupRole13Inheritance(); // Subscribe to Role-13 data changes
        this.setupStateListener(); // Listen for external state updates (e.g., from backend API)

        // CRITICAL: Wait for loadSavedState() to complete before showing the form
        // This prevents the visual "reload" effect where the form appears, then recalculates
        await this.loadSavedState();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);

        // CRITICAL: Only set isLoading = false AFTER all initialization is complete
        // This ensures the form only renders once with all data fully loaded
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      })
      .catch((error) => {
        // Classified rather than reported as a bare schema failure: this one
        // `.catch` also covers the cross-sheet preload, buildForm, the edit
        // pipeline and loadSavedState, so "Failed to load form schema" named
        // the wrong step for most of what can land here.
        this.loadTraceHandle.fail(classifySheetInitError(error), error);
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      });
  }

  /**
   * Build reactive form from schema
   */
  private buildForm(): void {
    if (!this.schema) return;

    const formControls: any = {};

    this.schema.controls.forEach((control) => {
      // Skip headers
      if (this.isSectionHeader(control)) return;

      // Determine required validators
      const validators: ValidatorFn[] = [];

      if (control.ui.dualColumn) {
        // Always create 4 columns: Consolidated (Current, Previous) and Separate (Current, Previous)
        // All fields start disabled (editable fields can be enabled via edit mode)
        // IMPORTANT: Initialize with null (not 0) so mandatory field validation can detect unfilled fields
        // PHASE 2: Updated to use bracket notation
        const defaultValue = null;
        formControls[
          this.getXbrlControlName(control.qname, 'Consolidated', 'Current')
        ] = new FormControl(
          { value: defaultValue, disabled: true },
          validators
        );
        formControls[
          this.getXbrlControlName(control.qname, 'Consolidated', 'Previous')
        ] = new FormControl(
          { value: defaultValue, disabled: true },
          validators
        );
        formControls[
          this.getXbrlControlName(control.qname, 'Separate', 'Current')
        ] = new FormControl(
          { value: defaultValue, disabled: true },
          validators
        );
        formControls[
          this.getXbrlControlName(control.qname, 'Separate', 'Previous')
        ] = new FormControl(
          { value: defaultValue, disabled: true },
          validators
        );
      }
    });

    this.statementOfProfitOrLossForm = this.fb.group(formControls);

    // CRITICAL: Immediately pull Role 13 values after form is built
    // This ensures linked fields start with Role 13's values, not 0
    this.pullInitialRole13Values();
  }

  /**
   * Pull Role 13 values immediately after form is built
   * This ensures linked fields are initialized with Role 13's calculated values
   */
  private pullInitialRole13Values(): void {
    const attemptPull = (attempt: number = 1): void => {
      const role13Data = this.stateService.getSheetData(
        SHEET_KEYS.analysisOfProfitOrLoss
      );

      // Check if data exists AND has meaningful calculated values
      const hasData = role13Data && Object.keys(role13Data).length > 0;
      const hasCalculatedValues =
        hasData && this.hasValidRole13CalculatedValues(role13Data!);

      if (hasData && hasCalculatedValues) {
        this.pullRole13Values(role13Data!);
      } else if (attempt < 10) {
        // Retry up to 10 times with increasing delays (100ms, 200ms, 300ms, ...)
        const delay = attempt * 100;
        setTimeout(() => attemptPull(attempt + 1), delay);
      } else {
        // Pull whatever data we have, even if calculated values are missing
        if (hasData) {
          this.pullRole13Values(role13Data!);
        }
      }
    };

    // Start first attempt immediately
    attemptPull(1);
  }

  /**
   * Check if Role 13 data has valid calculated values (not all zeros)
   * This helps detect if Role 13 component has initialized and run calculateTotals()
   */
  private hasValidRole13CalculatedValues(
    role13Data: Record<string, any>
  ): boolean {
    // Check key calculated fields that should have non-zero values if properly calculated.
    // Keys MUST use the bracket-notation cell suffix (`[Dim][Period]`) that the
    // state slice is actually keyed by — the prior `...SeparateCurrent` /
    // `...ConsolidatedCurrent` concatenated form never matched any key, so this
    // probe always returned false and the initial AoPL pull fell through to the
    // retry-then-pull fallback. Prefix is the MFRS taxonomy (`ifrs-full`); an
    // MPERS miss degrades to that same fallback, so the gate is never worse than
    // before and now actually detects readiness for MFRS conversions.
    const keyFields = [
      'ifrs-full:OtherExpenseByFunction[Separate][CurrentYear]',
      'ifrs-full:OtherExpenseByFunction[Consolidated][CurrentYear]',
    ];

    // If ANY of the key fields has a non-zero value, consider it valid
    const hasNonZeroValue = keyFields.some((field) => {
      const value = role13Data[field];
      return value !== undefined && value !== null && value !== 0;
    });

    return hasNonZeroValue;
  }

  /**
   * Check if control is a section header (including TextBlock)
   */
  isSectionHeader(control: FieldControl): boolean {
    return (
      control.type === 'SectionHeader' ||
      control.type === 'TextBlock' ||
      control.ui.controlType === 'section-header'
    );
  }

  /**
   * Whether the section header at `index` is collapsible — renders a chevron
   * and toggles on click. Delegates to the shared, structural
   * `isCollapsibleHeader` rule (any header deeper than the level-1 banner that
   * encloses rows — no level ceiling, so it can't drift as the schema gains
   * deeper headers). Default `minCollapsibleLevel` of 2 keeps the level-1
   * "Statement of Profit or Loss" banner non-toggled.
   */
  isCollapsibleHeader(index: number): boolean {
    return !!this.schema && isCollapsibleHeaderRule(
      this.schema.controls,
      index,
      (control) => this.isSectionHeader(control),
    );
  }

  /**
   * PHASE 2: Get XBRL control name using simplified bracket notation
   * Converts from old concatenated format to new bracket format
   *
   * OLD: qnameConsolidatedCurrent
   * NEW: qname[Consolidated][CurrentYear]
   *
   * @param qname The XBRL concept name
   * @param consolidatedOrSeparate 'Consolidated' or 'Separate'
   * @param period 'Current' or 'Previous'
   * @returns Control name in bracket format
   */
  getXbrlControlName(
    qname: string,
    consolidatedOrSeparate: 'Consolidated' | 'Separate',
    period: 'Current' | 'Previous'
  ): string {
    const periodLabel = period === 'Current' ? 'CurrentYear' : 'PreviousYear';
    return `${qname}[${consolidatedOrSeparate}][${periodLabel}]`;
  }

  /**
   * Get header CSS class based on level
   */
  getHeaderClass(level?: number | string): string {
    return headerLevelClass(level);
  }

  /**
   * Check if field is calculated
   */
  /**
   * Does this row's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory` so this sheet cannot drift from
   * the other 16 — the marker used to live only inside
   * `<label class="field-label">`, which this sheet does not render, so its
   * mandatory rows went unmarked from the initial scaffolding until 2026-08-11.
   * See `isControlMandatory` in `required-field.util.ts`.
   *
   * `crossSheetValue` is supplied because a cross-sheet `requiredIf` degrades to
   * "not mandatory" without it; every other declaration form this sheet uses is
   * unconditional and needs no context. Same-sheet `selfData` is deliberately
   * NOT passed: this sheet keys its values per cell
   * (`qname[Dimension][Period]`), not by bare qname, so a same-sheet lookup
   * would silently miss. Add it — with the right key shape — if a same-sheet
   * conditional rule is ever declared here.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  isCalculatedField(control: FieldControl): boolean {
    return control.ui.calculated === true;
  }

  /**
   * Get XBRL prefix (ifrs-full for MFRS, ifrs-smes for MPERS)
   * Uses CrossReferenceService to get the actual accounting standard from Role-0
   */
  private getXbrlPrefix(): string {
    const isMFRS = this.crossRefService.isMFRS();
    const prefix = isMFRS ? 'ifrs-full' : 'ifrs-smes';
    return prefix;
  }

  /**
   * Wire the live (in-edit) pipeline: calc → validateSameSheet → validateCrossSheet → evaluateStatus.
   * No persist — that runs only on Tick (saveEditMode).
   * Guard: skip while loading, calculating, or cancelling changes.
   */
  private setupEditPipeline(): void {
    buildEditModeLivePipeline({
      source$: this.statementOfProfitOrLossForm.valueChanges,
      debounceMs: FORM_CALCULATION_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState || this.isCalculating || this.isCancellingChanges,
      calculate: () => this.calculateTotals(),
      validateSameSheet: () => this.validateSameSheet(),
      validateCrossSheet: () => this.validateCrossSheet(),
      evaluateStatus: () => this.evaluateSheetStatus(),
    });
  }

  /**
   * SAME-SHEET validation — safe to run live during edit.
   * Clears and rebuilds only the same-sheet error sources (validationErrors keys NOT in
   * crossSheetErrorQnames, mandatoryFieldErrors); leaves cross-sheet (equalityValidationErrors
   * and the corresponding crossSheetErrorQnames entries in validationErrors) frozen.
   *
   * Classification note: validateConditionalMandatoryFields() reads another sheet only as a
   * static applicability condition (is this role-12 field required given what role-0 says?);
   * the error it raises is about THIS sheet's field being empty. It must update live so
   * mandatory-field failures display immediately while the user edits. Classifying it
   * CROSS-SHEET would silently defer mandatory feedback to Tick.
   */
  private validateSameSheet(): void {
    // Clear same-sheet portion of validationErrors (preserve cross-sheet frozen keys)
    for (const key of Array.from(this.validationErrors.keys())) {
      if (!this.crossSheetErrorQnames.has(key)) {
        this.validationErrors.delete(key);
      }
    }
    this.mandatoryFieldErrors.clear();

    this.validateMandatoryFields();
    this.validateConditionalMandatoryFields();
    this.validateAgainstApiData();

    // Trigger change detection after same-sheet validation
    this.validationErrors = new Map(this.validationErrors);
    this.cdr.detectChanges();
  }

  /**
   * CROSS-SHEET validation — runs only on Tick and from cross-sheet listeners.
   * Clears and rebuilds equalityValidationErrors and the crossSheetErrorQnames
   * tracking set, then merges the equality error messages into validationErrors.
   */
  private validateCrossSheet(): void {
    // Strip previous cross-sheet contributions from the shared map. A qname may
    // also carry a same-sheet suggested-change warning (EXTRACTED_SUM_MARKER) —
    // keep that, drop only the cross-sheet equality messages.
    this.crossSheetErrorQnames.forEach((qname) => {
      const errors = this.validationErrors.get(qname);
      if (!errors) return;
      const sameSheetOnly = errors.filter((message) => message.includes(EXTRACTED_SUM_MARKER));
      if (sameSheetOnly.length > 0) {
        this.validationErrors.set(qname, sameSheetOnly);
      } else {
        this.validationErrors.delete(qname);
      }
    });
    this.crossSheetErrorQnames.clear();
    this.equalityValidationErrors.clear();

    this.runEqualityValidations();

    // runEqualityValidations already merged its errors into validationErrors;
    // record which qnames it touched so validateSameSheet can freeze them.
    this.equalityValidationErrors.forEach((_unusedErrors, qname) => this.crossSheetErrorQnames.add(qname));

    this.validationErrors = new Map(this.validationErrors);
    this.cdr.detectChanges();
  }

  /**
   * Run all validation methods in sequence, then trigger a single change detection.
   * This prevents the flicker caused by intermediate detectChanges calls that
   * temporarily render the template without errors from later validation methods.
   *
   * Developer Note — REUSABLE mount-vs-pure parity diagnostic (kept commented):
   * The `[Role12 SOPL]…` traces in runEqualityValidations() and evaluateSheetStatus()
   * were added to chase a ParityCheck mismatch. Uncomment to re-enable.
   */
  private runAllValidations(): void {
    this.validateSameSheet();
    this.validateCrossSheet();
    this.evaluateSheetStatus();
  }

  /**
   * Setup listener for external state updates (e.g., when backend API data is populated)
   */
  private setupStateListener(): void {
    this.stateService.modifiedState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        // Skip re-validation when this component triggered the state update itself
        // (setupStatePersistence already ran all validations via setupCalculations)
        if (this.isStateSelfUpdate) {
          return;
        }

        // After initial load completes, skip re-patching. Past this point the
        // only emissions on this slice are commits — this sheet's own (already
        // dropped by `isStateSelfUpdate` above) and, via
        // `FilingFormStateService.applyRemoteChange`, a sibling tab's. A
        // re-patch here would overwrite the typed-but-unsaved values of rows
        // the user has OPEN (inline edit-mode sheets commit on Tick, so those
        // edits live only in the form while state still holds the pre-edit
        // number). Adopting a sibling's values is `adoptRemoteState()`'s job —
        // it runs later, on `remoteUpdateApplied$`, and skips open rows.
        // Mirrors role23/role24. See isInitialLoadComplete.
        if (this.isInitialLoadComplete) {
          return;
        }

        const newData = state.statementOfProfitOrLoss;

        // Only reload if we have new data AND form is not currently being edited
        // Also skip during cancel flow to prevent chain reactions of validation
        if (
          newData &&
          Object.keys(newData).length > 0 &&
          !this.isLoadingState &&
          !this.isCancellingChanges
        ) {
          this.isLoadingState = true;

          // CRITICAL: Filter out linked fields from API/DB data
          // Linked fields should ONLY come from Role 13, never from database
          const filteredData = this.filterOutLinkedFields(newData);

          this.statementOfProfitOrLossForm.patchValue(filteredData, {
            emitEvent: false,
          });

          // IMPORTANT: Mark form as pristine after loading to prevent showing 'Edited' status
          this.statementOfProfitOrLossForm.markAsPristine();

          // Verify data was actually patched
          const afterPatch = this.statementOfProfitOrLossForm.getRawValue();
          const sampleAfter = Object.entries(afterPatch)
            .filter(([k, v]) => v !== 0 && v !== null)
            .slice(0, 5);

          // CRITICAL: Re-pull Role 13 values AFTER patching state
          // This ensures linked fields always show current Role 13 values
          const role13Data = this.stateService.getSheetData(
            SHEET_KEYS.analysisOfProfitOrLoss
          );
          if (role13Data && Object.keys(role13Data).length > 0) {
            this.pullRole13Values(role13Data);
          }

          // Recalculate totals and validate
          setTimeout(() => {
            this.calculateTotals();
            this.runAllValidations();
            // Mark as pristine again after calculations
            this.statementOfProfitOrLossForm.markAsPristine();
            this.isLoadingState = false;
          }, 100);
        }
      });

    // Listen for filing-info + scopeOfFiling changes (cross-sheet
    // re-validation watch — golden pattern hash + distinctUntilChanged).
    // Role-12 reads the filing-info slice for `NatureOfFinancialStatements`
    // (column visibility via `dimensionVisibility`, and the mandatory rules in
    // `validateConditionalMandatoryFields`) and scopeOfFiling for the
    // consolidation toggle (`checkConsolidatedStatus`). Same-tab SPA
    // navigation back to this sheet should re-validate when either upstream
    // slice has changed.
    //
    // BOTH filing-info slots are hashed. Role 12 is Phase-2-only, where
    // hydration writes to role 1 (`filingInfo`) and leaves the role-0
    // (`filingPrerequisites`) slot an empty stub — hashing role 0 alone meant
    // the hash never changed and a Nature edit on Filing Info never re-ran this
    // sheet's validations. Same root cause as the `requiredIf` resolution fix
    // in `validateConditionalMandatoryFields`.
    this.stateService.modifiedState$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfProfitOrLoss)),
        map(state => JSON.stringify({
          r0: state.filingPrerequisites,
          r1: state.filingInfo,
          r2: state.scopeOfFiling,
        })),
        distinctUntilChanged(),
        debounceTime(CROSS_SHEET_VALIDATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.isLoadingState) {
          this.validateCrossSheet();
          this.evaluateSheetStatus();
        }
      });

    // ----------------------------------
    // Cross-tab safety net — sibling-tab edits to filingPrerequisites or
    // scopeOfFiling must trigger re-validation here too.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfProfitOrLoss)),
        filter(() => !!this.schema && !!this.statementOfProfitOrLossForm),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey, changedSheets }) => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role12-statementOfProfitOrLoss',
          //   sourceSheetKey: sheetKey,
          // });
        }

        // Own-slice change -> repaint this sheet's fields. Other-slice change ->
        // fall through to the cross-sheet revalidation tail only, as before.
        if (changedSheets.includes(SHEET_KEYS.statementOfProfitOrLoss))
          this.adoptRemoteState();

        this.validateCrossSheet();
        this.evaluateSheetStatus();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption for role12 (SoPL). Rows currently open in edit mode are
   * skipped — the user's in-flight work wins until they tick (local overwrites
   * and broadcasts) or press X (the row lands on the adopted value; see
   * `cancelEditMode`).
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoInlineEditSheet`.
   * `calculateTotals()` here recomputes for DISPLAY only; the sibling already
   * persisted its own recomputed totals.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    const openControlNames = [...this.editingRows.values()]
      .flatMap(snapshot => Object.keys(snapshot ?? {}));

    adoptIntoInlineEditSheet(
      this.statementOfProfitOrLossForm,
      this.stateService.getSheetData(SHEET_KEYS.statementOfProfitOrLoss),
      openControlNames
    );

    this.calculateTotals();
    this.validateSameSheet();
  }

  private calculateTotals(): void {
    if (!this.schema) return;

    this.isCalculating = true;

    // Get taxonomy prefix (ifrs-full for MFRS, ifrs-smes for MPERS)
    const prefix = this.getXbrlPrefix() as 'ifrs-full' | 'ifrs-smes';

    // Get all current form values
    const allFormData = this.statementOfProfitOrLossForm.getRawValue();

    // Call the calculation service
    const calculatedTotals = this.calculationService.calculateTotals_310000(
      allFormData,
      prefix
    );

    // Register calculated keys so isSheetModified() skips them
    this.calculatedTotalKeys = Object.keys(calculatedTotals);
    this.stateService.registerCalculatedKeys(
      SHEET_KEYS.statementOfProfitOrLoss,
      Object.keys(calculatedTotals)
    );

    // Apply calculated values to form controls
    Object.entries(calculatedTotals).forEach(([controlName, value]) => {
      const control = this.statementOfProfitOrLossForm.get(controlName);
      if (control) {
        control.setValue(value, { emitEvent: false });
        // Mark as pristine to prevent calculated fields from showing 'Edited' status
        control.markAsPristine();
      }
    });

    this.isCalculating = false;
  }

  /**
   * Get field value by qname and suffix
   */
  /**
   * Convert simple suffix format to bracket notation
   * @param suffix Simple format like 'ConsolidatedCurrent'
   * @returns Consolidated/Separate and Current/Previous parts
   */
  private parseSuffix(suffix: string): {
    consolidatedOrSeparate: 'Consolidated' | 'Separate';
    period: 'Current' | 'Previous';
  } {
    if (suffix.includes('Consolidated')) {
      return {
        consolidatedOrSeparate: 'Consolidated',
        period: suffix.includes('Current') ? 'Current' : 'Previous',
      };
    } else {
      return {
        consolidatedOrSeparate: 'Separate',
        period: suffix.includes('Current') ? 'Current' : 'Previous',
      };
    }
  }

  private getFieldValue(qname: string, suffix: string): number {
    // Convert simple suffix to bracket notation
    const { consolidatedOrSeparate, period } = this.parseSuffix(suffix);
    const controlName = this.getXbrlControlName(
      qname,
      consolidatedOrSeparate,
      period
    );
    const control = this.statementOfProfitOrLossForm.get(controlName);

    if (!control) {
      return 0;
    }

    const value = control.value;

    // IMPORTANT: Convert to number to prevent string concatenation (e.g., "1" + "1" = "11")
    // parseFloat handles null/undefined by returning NaN, so we use || 0 as fallback
    const numericValue = parseFloat(value);
    return isNaN(numericValue) ? 0 : numericValue;
  }

  /**
   * Subscribe to Role-13 (Analysis of Profit or Loss) state changes and pull values into linked fields
   * CRITICAL: This ensures all fields with linkTo.role='310100' display values from Role 13
   */
  private setupRole13Inheritance(): void {
    // STEP 1: Load existing Role-13 data immediately
    const initialRole13Data = this.stateService.getSheetData(
      SHEET_KEYS.analysisOfProfitOrLoss
    );
    if (initialRole13Data && Object.keys(initialRole13Data).length > 0) {
      // Check for OtherExpenseByFunction in initial data
      this.pullRole13Values(initialRole13Data);
    }

    // STEP 2: Subscribe to future Role-13 data changes (from user edits or calculations)
    this.stateService.state$
      .pipe(
        map((state) => state.analysisOfProfitOrLoss),
        debounceTime(STATE_INHERITANCE_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe((role13Data) => {
        if (!role13Data || Object.keys(role13Data).length === 0) {
          return;
        }

        this.pullRole13Values(role13Data);
      });

    // STEP 3: Subscribe to backend data loading for Role 13
    // This detects when API data arrives for Role 13, then waits for calculations to complete
    this.stateService.modifiedState$
      .pipe(
        map((state) => state.analysisOfProfitOrLoss),
        debounceTime(STATE_INHERITANCE_FAST_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe((role13BackendData) => {
        if (!role13BackendData || Object.keys(role13BackendData).length === 0) {
          return;
        }

        // Wait 300ms for Role 13 component to:
        // 1. Initialize (if it exists)
        // 2. Filter calculated fields
        // 3. Calculate totals
        // 4. Save to state
        // Then pull the calculated values
        setTimeout(() => {
          const updatedRole13Data = this.stateService.getSheetData(
            SHEET_KEYS.analysisOfProfitOrLoss
          );
          if (updatedRole13Data && Object.keys(updatedRole13Data).length > 0) {
            this.pullRole13Values(updatedRole13Data);
          }
        }, 300);
      });
  }

  /**
   * Pull values from Role-13 data into Role-12 linked fields
   * This is the core mechanism that ensures linked fields display values from Role 13
   */
  private pullRole13Values(role13Data: Record<string, any>): void {
    if (!this.schema) {
      return;
    }

    // Find all controls with linkTo configuration pointing to Role-13 (role: '310100')
    const linkedControls = this.schema.controls.filter(
      (control) => control.ui.linkTo && control.ui.linkTo.role === '310100'
    );

    if (linkedControls.length === 0) {
      return;
    }

    let inheritedCount = 0;
    const linkedFieldNames: string[] = [];

    // Update each linked field with data from Role-13
    linkedControls.forEach((control) => {
      const sourceQname = control.ui.linkTo!.qname;
      linkedFieldNames.push(`${control.label} (${sourceQname})`);

      // Update all 4 columns (Consolidated Current, Consolidated Previous, Separate Current, Separate Previous)
      // IMPORTANT: Use bracket notation to match form control names and state data keys
      const suffixes = [
        '[Consolidated][CurrentYear]',
        '[Consolidated][PreviousYear]',
        '[Separate][CurrentYear]',
        '[Separate][PreviousYear]',
      ];

      suffixes.forEach((suffix) => {
        // Build control name for both Role-13 and Role-12 (bracket notation)
        const role13ControlName = `${sourceQname}${suffix}`;
        const role12ControlName = `${control.qname}${suffix}`;

        // Get value from Role-13 data
        const role13Value = role13Data[role13ControlName];

        if (role13Value !== undefined && role13Value !== null) {
          // Update Role-12 form control
          const formControl =
            this.statementOfProfitOrLossForm.get(role12ControlName);

          if (formControl) {
            // CRITICAL: Set value without triggering valueChanges to avoid infinite loops
            formControl.setValue(role13Value, { emitEvent: false });
            // IMPORTANT: Mark as pristine to prevent showing 'Edited' status for inherited values
            formControl.markAsPristine();
            inheritedCount++;
          }
        }
      });
    });
  }

  /**
   * Load saved state from service
   */
  private loadSavedState(): Promise<void> {
    return new Promise((resolve) => {
      this.isLoadingState = true;
      this.stateService.markSheetInitializing(SHEET_KEYS.statementOfProfitOrLoss);
      const savedData = this.stateService.getSheetData(SHEET_KEYS.statementOfProfitOrLoss);

      if (savedData && Object.keys(savedData).length > 0) {
        // CRITICAL: Filter out linked fields from saved state
        // Linked fields should ONLY come from Role 13, never from saved state
        const filteredData = this.filterOutLinkedFields(savedData);
        this.statementOfProfitOrLossForm.patchValue(filteredData, {
          emitEvent: false,
        });

        // IMPORTANT: Mark form as pristine after loading to prevent showing 'Edited' status on initial load
        this.statementOfProfitOrLossForm.markAsPristine();

        // Verify data was actually patched
        const afterPatch = this.statementOfProfitOrLossForm.getRawValue();
        const sampleAfter = Object.entries(afterPatch)
          .filter(([k, v]) => v !== 0 && v !== null)
          .slice(0, 5);

        // CRITICAL: Re-pull Role 13 values AFTER loading saved state
        // This ensures linked fields always show current Role 13 values,
        // even if saved state has stale data
        const role13Data = this.stateService.getSheetData(
          SHEET_KEYS.analysisOfProfitOrLoss
        );
        if (role13Data && Object.keys(role13Data).length > 0) {
          this.pullRole13Values(role13Data);
        }

        // Recalculate totals and validate after loading
        setTimeout(() => {
          this.calculateTotals();

          // Save calculated values to state so other sheets (e.g., Role 19 SOCIE)
          // read correct ProfitLoss for cross-sheet validation even if user navigates immediately
          const allFormData = this.statementOfProfitOrLossForm.getRawValue();
          const filteredData = this.filterOutLinkedFields(allFormData);
          this.stateService.updateSheetData(SHEET_KEYS.statementOfProfitOrLoss, filteredData, undefined, true);

          this.runAllValidations();
          // Mark as pristine again after calculations to ensure clean state on load
          this.statementOfProfitOrLossForm.markAsPristine();
          this.isLoadingState = false;
          this.isValidationComplete = true;

          // Flush full form state to modified state before snapshotting baseline
          // This ensures original and modified start with the same key set
          setTimeout(() => {
            this.isLoadingState = true; // Guard against setupStateListener reacting
            const fullFormData = this.statementOfProfitOrLossForm.getRawValue();
            const filteredData = this.filterOutLinkedFields(fullFormData);
            this.stateService.updateSheetData(SHEET_KEYS.statementOfProfitOrLoss, filteredData, undefined, true);
            this.evaluateSheetStatus();
            this.stateService.markSheetInitialized(SHEET_KEYS.statementOfProfitOrLoss);
            this.isLoadingState = false;
            // Initial load has settled — from here the state listener stops
            // re-patching so a foreign commit can't clobber in-edit rows.
            this.isInitialLoadComplete = true;
          }, 600);

          resolve(); // Resolve the promise after all initialization is complete
        }, 100);
      } else {
        // Even if no saved state, still pull Role 13 values
        const role13Data = this.stateService.getSheetData(
          SHEET_KEYS.analysisOfProfitOrLoss
        );
        if (role13Data && Object.keys(role13Data).length > 0) {
          this.pullRole13Values(role13Data);
        }

        // IMPORTANT: Still calculate totals and run validation even when no saved state
        // This ensures calculated fields show 0.00 instead of blank, and mandatory field errors are detected
        setTimeout(() => {
          this.calculateTotals(); // Calculate totals so calculated fields show 0.00 instead of blank
          this.runAllValidations();
          this.isLoadingState = false;
          this.isValidationComplete = true;

          // Flush full form state to modified state before snapshotting baseline
          setTimeout(() => {
            this.isLoadingState = true; // Guard against setupStateListener reacting
            const fullFormData = this.statementOfProfitOrLossForm.getRawValue();
            const filteredData = this.filterOutLinkedFields(fullFormData);
            this.stateService.updateSheetData(SHEET_KEYS.statementOfProfitOrLoss, filteredData, undefined, true);
            this.evaluateSheetStatus();
            this.stateService.markSheetInitialized(SHEET_KEYS.statementOfProfitOrLoss);
            this.isLoadingState = false;
            // Initial load has settled — from here the state listener stops
            // re-patching so a foreign commit can't clobber in-edit rows.
            this.isInitialLoadComplete = true;
          }, 600);

          resolve(); // Resolve the promise after all initialization is complete
        }, 100);
      }
    });
  }

  /**
   * Auto-fill form with demo data
   */
  autoFillDemo(): void {
    if (!this.statementOfProfitOrLossForm) return;

    const demoData: any = {};

    // Helper function to set values for all 4 columns
    const setDemoValues = (
      qname: string,
      consolidatedCurrentVal: number,
      consolidatedPreviousVal: number,
      separateCurrentVal: number,
      separatePreviousVal: number
    ) => {
      demoData[this.getXbrlControlName(qname, 'Consolidated', 'Current')] =
        consolidatedCurrentVal;
      demoData[this.getXbrlControlName(qname, 'Consolidated', 'Previous')] =
        consolidatedPreviousVal;
      demoData[this.getXbrlControlName(qname, 'Separate', 'Current')] =
        separateCurrentVal;
      demoData[this.getXbrlControlName(qname, 'Separate', 'Previous')] =
        separatePreviousVal;
    };

    // Revenue and Cost of Sales
    setDemoValues('ssmt:Revenue', 50000000, 45000000, 48000000, 43000000);
    setDemoValues(
      'ssmt:CostOfSales',
      -32000000,
      -29000000,
      -31000000,
      -28000000
    );

    // Other Income
    setDemoValues('ssmt:OtherIncome', 500000, 450000, 450000, 400000);

    // Operating Expenses (by function)
    setDemoValues(
      'ssmt:DistributionCosts',
      -2500000,
      -2200000,
      -2400000,
      -2100000
    );
    setDemoValues(
      'ssmt:AdministrativeExpenses',
      -5000000,
      -4500000,
      -4800000,
      -4300000
    );
    setDemoValues('ssmt:OtherExpenses', -300000, -250000, -280000, -230000);

    // Finance
    setDemoValues('ssmt:FinanceCosts', -800000, -750000, -780000, -730000);
    setDemoValues('ssmt:FinanceIncome', 100000, 90000, 95000, 85000);

    // Share of Associates/JVs (consolidated only)
    if (this.isConsolidated) {
      setDemoValues('ssmt:ShareOfProfitOfAssociates', 200000, 180000, 0, 0);
      setDemoValues('ssmt:ShareOfProfitOfJointVentures', 150000, 120000, 0, 0);
    } else {
      setDemoValues('ssmt:ShareOfProfitOfAssociates', 0, 0, 0, 0);
      setDemoValues('ssmt:ShareOfProfitOfJointVentures', 0, 0, 0, 0);
    }

    // Income Tax Expense
    setDemoValues(
      'ssmt:IncomeTaxExpense',
      -2500000,
      -2200000,
      -2400000,
      -2100000
    );

    // Attribution (using new column names)
    setDemoValues(
      'ssmt:ProfitAttributableToOwnersOfParent',
      7650000,
      6790000,
      7087000,
      6255000
    );
    setDemoValues(
      'ssmt:ProfitAttributableToNonControllingInterests',
      200000,
      150000,
      0,
      0
    );

    // Earnings per share (sen)
    setDemoValues('ssmt:BasicEarningsPerShare', 15.3, 13.58, 14.17, 12.51);
    setDemoValues('ssmt:DilutedEarningsPerShare', 15.1, 13.4, 14.0, 12.35);

    this.statementOfProfitOrLossForm.patchValue(demoData);

    // Trigger calculations
    setTimeout(() => this.calculateTotals(), 100);

    this.messageService.add({
      severity: 'success',
      summary: 'Demo Data Loaded',
      detail:
        'Form filled with Consolidated and Separate demo data (Current and Previous Year)',
      life: TOAST_DURATION_DEFAULT_MS,
    });
  }

  /**
   * Clear form
   */
  clearForm(): void {
    this.statementOfProfitOrLossForm.reset();
    this.stateService.clearSheetData(SHEET_KEYS.statementOfProfitOrLoss);

    this.messageService.add({
      severity: 'info',
      summary: 'Form Cleared',
      detail: 'All fields have been reset',
      life: TOAST_DURATION_SHORT_MS,
    });
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.statementOfProfitOrLossForm.valid) {
      // Get raw data (includes all fields including disabled and _2 fields)
      const rawData = this.statementOfProfitOrLossForm.getRawValue();

      // Filter out linked fields and _2 validation fields before submission
      const filteredData = this.filterForBackendSubmission(rawData);

      // Update state with filtered data
      this.stateService.updateSheetData(
        SHEET_KEYS.statementOfProfitOrLoss,
        filteredData
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Statement of profit or loss data saved successfully',
        life: TOAST_DURATION_DEFAULT_MS,
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please check all required fields',
        life: TOAST_DURATION_DEFAULT_MS,
      });
    }
  }

  /**
   * Check if cross-validation errors exist (ProfitLoss vs ProfitLoss_2 mismatch)
   * These errors block submission
   *
   * @returns true if calculation mismatch exists
   */
  private hasCalculationMismatch(): boolean {
    const prefix = this.getXbrlPrefix();

    // Check if validation errors for ProfitLoss or ProfitLoss_2 contain cross-validation error message
    const profitLossErrors =
      this.validationErrors.get(`${prefix}:ProfitLoss`) || [];
    const profitLoss2Errors =
      this.validationErrors.get(`${prefix}:ProfitLoss_2`) || [];

    // Check if any error message contains the cross-validation error text
    const hasCrossValidationError = (errors: string[]) =>
      errors.some((error) =>
        error.includes('Mismatch in value provided for the same field')
      );

    return (
      hasCrossValidationError(profitLossErrors) ||
      hasCrossValidationError(profitLoss2Errors)
    );
  }

  /**
   * Determine if submit button should be enabled
   * Disabled if: cross-validation errors exist OR form is invalid
   *
   * @returns true if form can be submitted
   */
  canSubmit(): boolean {
    return (
      this.statementOfProfitOrLossForm.valid && !this.hasCalculationMismatch()
    );
  }

  /**
   * Filter out linked fields from data object
   * Linked fields should ONLY come from Role 13, never from saved state or API data
   * This ensures Role 13 calculated values are always used for linked fields
   */
  private filterOutLinkedFields(
    data: Record<string, any>
  ): Record<string, any> {
    if (!this.schema) return data;

    const filteredData: Record<string, any> = {};
    // IMPORTANT: Use bracket notation to match form control names and state data keys
    const suffixes = [
      '[Consolidated][CurrentYear]',
      '[Consolidated][PreviousYear]',
      '[Separate][CurrentYear]',
      '[Separate][PreviousYear]',
    ];

    // Get all linked controls from schema
    const linkedQnames = this.schema.controls
      .filter((control) => control.ui.linkTo?.role === '310100')
      .map((control) => control.qname);

    // Build a set of all linked field keys for fast lookup
    const linkedKeys = new Set<string>();
    for (const qname of linkedQnames) {
      for (const suffix of suffixes) {
        linkedKeys.add(`${qname}${suffix}`);
      }
    }

    // Filter out linked fields
    Object.keys(data).forEach((key) => {
      if (!linkedKeys.has(key)) {
        filteredData[key] = data[key];
      }
    });

    return filteredData;
  }

  /**
   * Filter out validation fields (fields ending with _2) from data object
   * These fields are frontend-only for cross-validation and should not be sent to backend
   *
   * @param data - Form data object with all fields
   * @returns Filtered data object without _2 suffix fields
   */
  private filterOutValidationFields(
    data: Record<string, any>
  ): Record<string, any> {
    const filtered: Record<string, any> = {};

    Object.keys(data).forEach((key) => {
      // Extract base qname before bracket notation
      // Example: "ifrs-full:ProfitLoss_2[Consolidated][CurrentYear]" -> "ifrs-full:ProfitLoss_2"
      const baseQname = key.split('[')[0];

      // Only include if qname doesn't end with _2
      if (!baseQname.endsWith('_2')) {
        filtered[key] = data[key];
      }
    });

    return filtered;
  }

  /**
   * Comprehensive filter for backend submission
   * Removes both linked fields (from Role 13) and validation fields (_2 suffix)
   *
   * @param data - Raw form data
   * @returns Filtered data ready for backend submission
   */
  private filterForBackendSubmission(
    data: Record<string, any>
  ): Record<string, any> {
    // First filter out linked fields
    let filtered = this.filterOutLinkedFields(data);

    // Then filter out validation fields (_2 suffix)
    filtered = this.filterOutValidationFields(filtered);

    return filtered;
  }

  /**
   * Check if a row is currently in edit mode
   */
  isRowInEditMode(control: FieldControl): boolean {
    return this.editingRows.has(control.qname);
  }

  /**
   * Enter edit mode for a row - store original values, enable controls, and mark as editing
   */
  enterEditMode(control: FieldControl): void {
    // Don't allow editing before validation is complete
    if (!this.isValidationComplete) {
      return;
    }
    // Don't allow editing calculated or readonly fields
    if (this.isCalculatedField(control) || control.ui.readonly) {
      return;
    }

    // Capture the committed badge BEFORE entering edit mode (isRowInEditMode is still false here).
    this.frozenRowStatus.set(control.qname, this.getRowValidationStatus(control));

    // Get all 4 controls for this row
    const controlNames = [
      this.getXbrlControlName(control.qname, 'Consolidated', 'Current'),
      this.getXbrlControlName(control.qname, 'Consolidated', 'Previous'),
      this.getXbrlControlName(control.qname, 'Separate', 'Current'),
      this.getXbrlControlName(control.qname, 'Separate', 'Previous'),
    ];

    // Store original values
    const originalValues: { [key: string]: number | null } = {};
    controlNames.forEach((name) => {
      originalValues[name] =
        this.statementOfProfitOrLossForm.get(name)?.value ?? null;
    });
    this.editingRows.set(control.qname, originalValues);
    this.stateService.markSheetEditing(SHEET_KEYS.statementOfProfitOrLoss);

    // Enable all 4 controls, revealing only the columns this permutation SHOWS —
    // a hidden required column is permanently null, so enabling makes it invalid
    // and revealing it would paint the row red with no text to explain it.
    enableDualColumnRowAndRevealVisible(
      this.statementOfProfitOrLossForm,
      control.qname,
      this.dimensionVisibility,
      { emitEvent: true }
    );
  }

  /**
   * Save edit mode (Tick) — disable controls, subset-persist only this row's 4 controlNames
   * (with linked fields filtered), then run same-sheet + cross-sheet validation and reconcile.
   */
  async saveEditMode(control: FieldControl): Promise<void> {
    if (!this.editingRows.has(control.qname)) {
      return;
    }

    // Get all 4 controls for this row
    const controlNames = [
      this.getXbrlControlName(control.qname, 'Consolidated', 'Current'),
      this.getXbrlControlName(control.qname, 'Consolidated', 'Previous'),
      this.getXbrlControlName(control.qname, 'Separate', 'Current'),
      this.getXbrlControlName(control.qname, 'Separate', 'Previous'),
    ];

    // Mark controls as pristine and disable them
    controlNames.forEach((name) => {
      const formControl = this.statementOfProfitOrLossForm.get(name);
      if (formControl) {
        formControl.markAsPristine();
        formControl.disable();
      }
    });

    // Remove from editing set
    this.frozenRowStatus.delete(control.qname);
    this.editingRows.delete(control.qname);

    // Recalculate totals so ProfitLoss etc. reflect the committed values
    this.calculateTotals();

    // Subset-persist: only this row's 4 controlNames, with linked fields stripped.
    // CRITICAL: filterOutLinkedFields must be applied to the subset so role13-inherited
    // keys are never written into role12's modified state (they were excluded on init too).
    const subset = this.filterOutLinkedFields(
      pickSubset(this.statementOfProfitOrLossForm.getRawValue(), [...controlNames, ...this.calculatedTotalKeys])
    );
    this.isStateSelfUpdate = true;
    this.stateService.updateSheetData(
      SHEET_KEYS.statementOfProfitOrLoss,
      subset,
      true // Force-persist the modified slot (IndexedDB via FilingFormPersistenceService)
    );
    this.isStateSelfUpdate = false;

    this.validateSameSheet();
    this.validateCrossSheet();
    this.stateService.reconcileEditedFlag(SHEET_KEYS.statementOfProfitOrLoss);
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfProfitOrLoss);
    this.evaluateSheetStatus();
  }

  /**
   * Cancel edit mode - always revert to the snapshot captured on enterEditMode,
   * then disable controls. Calc + same-sheet validation run synchronously (no setTimeout).
   * Cross-sheet validation is NOT re-run (nothing was committed).
   */
  cancelEditMode(control: FieldControl): void {
    const originalValues = this.editingRows.get(control.qname);
    if (!originalValues) {
      return;
    }

    // Revert to the LATEST ADOPTED state rather than the entry-time snapshot, so a
    // sibling tab's confirmed edit that arrived while this row was open is honoured
    // on cancel (P.O. rule 2026-08-17). Falls back to the entry snapshot for any
    // control the state does not carry.
    //
    // In the single-tab case the two are identical — the row is disabled and
    // unwritten for the whole time it is open — so this is a provable no-op there.
    const adopted = this.stateService.getSheetData(SHEET_KEYS.statementOfProfitOrLoss);
    Object.keys(originalValues).forEach((name) => {
      const formControl = this.statementOfProfitOrLossForm.get(name);
      if (formControl) {
        const revertValue = name in adopted
          ? adopted[name]
          : (originalValues[name] ?? null);
        formControl.setValue(revertValue, { emitEvent: false });
        formControl.markAsPristine();
        formControl.disable();
      }
    });

    // Remove from editing set before recalc so forceEdited is no longer set
    this.frozenRowStatus.delete(control.qname);
    this.editingRows.delete(control.qname);
    this.calculateTotals();
    this.validateSameSheet();
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfProfitOrLoss);
    this.evaluateSheetStatus();
  }

  /**
   * Validate calculated totals against baseline data
   * Populates validationErrors Map for yellow highlighting and error descriptions
   */
  private validateAgainstApiData(): void {
    // Skip validation during cancel flow - form is reverting to original values
    if (this.isCancellingChanges) {
      return;
    }

    // Compare display values against the immutable N8N snapshot (the extracted values)
    // via the shared ExtractedSumWarningService. Its `isSummableMonetaryTotal` predicate
    // narrows the warning to calculated/readonly monetary TOTALS only — plain editable
    // line items are no longer flagged (the previous inline loop wrongly compared every
    // non-header control). The service short-circuits to an empty map when the backend
    // has not yet supplied a snapshot (pre-rollout), preserving the prior skip behaviour.
    const columns: { key: string; label: string }[] = [];
    if (this.showConsolidatedColumns)                         columns.push({ key: 'Consolidated|CurrentYear',  label: 'Consolidated Current' });
    if (this.showConsolidatedColumns && this.hasPreviousYear) columns.push({ key: 'Consolidated|PreviousYear', label: 'Consolidated Previous' });
    columns.push({ key: 'Separate|CurrentYear', label: 'Separate Current' });
    if (this.hasPreviousYear)                                 columns.push({ key: 'Separate|PreviousYear', label: 'Separate Previous' });

    const warnings = this.extractedSumWarning.computeMismatchWarnings({
      sheetKey: SHEET_KEYS.statementOfProfitOrLoss,
      controls: this.schema?.controls ?? [],
      columns,
      getControlName: (qname, key) => {
        const [cs, period] = key.split('|');
        return `${qname}[${cs}][${period}]`;
      },
      getCurrentValue: (name) => this.statementOfProfitOrLossForm.get(name)?.value,
    });

    this.extractedSumWarning.mergeIntoValidationErrors(this.validationErrors, this.schema?.controls ?? [], warnings);

    // Propagate mismatch warnings between same-sheet equality-linked fields
    // Uses schema's equality validation rules to find linked fields (e.g., ProfitLoss ↔ ProfitLoss_2)
    // This avoids hardcoding qnames and makes the logic driven by the JSON schema
    if (this.schema) {
      this.equalityValidation.propagateSameSheetMismatchWarnings(
        this.schema,
        this.validationErrors,
        SHEET_KEYS.statementOfProfitOrLoss
      );
    }

    // NEW: Cross-validation check for ProfitLoss vs ProfitLoss_2
    // Both calculations should match - if not, show ERROR (blocks submission)
    const prefix = this.getXbrlPrefix(); // ifrs-full or ifrs-smes

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(
    //   '🔍 Cross-validation: Starting ProfitLoss vs ProfitLoss_2 validation'
    // );

    const suffixesToValidate = [
      {
        suffix: 'ConsolidatedCurrent',
        consolidatedOrSeparate: 'Consolidated' as const,
        period: 'Current' as const,
        label: 'Consolidated Current',
        show: this.showConsolidatedColumns,
      },
      {
        suffix: 'ConsolidatedPrevious',
        consolidatedOrSeparate: 'Consolidated' as const,
        period: 'Previous' as const,
        label: 'Consolidated Previous',
        show: this.showConsolidatedColumns && this.hasPreviousYear,
      },
      {
        suffix: 'SeparateCurrent',
        consolidatedOrSeparate: 'Separate' as const,
        period: 'Current' as const,
        label: 'Separate Current',
        show: true,
      },
      {
        suffix: 'SeparatePrevious',
        consolidatedOrSeparate: 'Separate' as const,
        period: 'Previous' as const,
        label: 'Separate Previous',
        show: this.hasPreviousYear,
      },
    ];

    const profitLossErrors: string[] = [];
    const profitLoss2Errors: string[] = [];

    const taxonomy: 'MFRS' | 'MPERS' = this.crossRefService.isMFRS() ? 'MFRS' : 'MPERS';

    // GLOBAL RULE (PO, 2026-06-19, REVISED) — this BLOCKING same-sheet
    // ProfitLoss <-> ProfitLoss_2 check compares the LIVE (recompute-of-latest)
    // form values, NEVER the N8N snapshot. The form already holds the Role-13-
    // inherited recompute (the editor patches calculateTotals_310000 in before
    // this runs), so ProfitLoss and ProfitLoss_2 are the same operands the pure
    // validator compares via `equalitySnapshot` — keeping the in-sheet display in
    // lockstep with the dropdown's pure verdict.
    //
    // An earlier revision baseline-anchored these operands (recompute the snapshot's
    // own leaves, then let the stored totals win). That made the SNAPSHOT a
    // cross-field comparison operand, which the PO rule forbids: an internally-
    // inconsistent extraction (stored ProfitLoss 195.08 vs its own leaves' 196.60,
    // Perm 04) then re-raised a false block. The recompute-vs-extracted gap is
    // surfaced ONLY as the non-blocking "does not match the extracted sum"
    // Suggested Change (above). A genuine live mismatch still blocks here.
    // See role12-statement-of-profit-or-loss.validator.ts.
    suffixesToValidate.forEach(
      ({ suffix, consolidatedOrSeparate, period, label, show }) => {
        if (!show) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`🔍 Cross-validation: Skipping ${label} (not visible)`);
          return;
        }

        // Use bracket notation to access controls
        const profitLossControlName = this.getXbrlControlName(
          `${prefix}:ProfitLoss`,
          consolidatedOrSeparate,
          period
        );
        const profitLoss2ControlName = this.getXbrlControlName(
          `${prefix}:ProfitLoss_2`,
          consolidatedOrSeparate,
          period
        );

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`🔍 Cross-validation: Checking ${label}`, {
        //   profitLossControlName,
        //   profitLoss2ControlName,
        // });

        const profitLossControl = this.statementOfProfitOrLossForm.get(
          profitLossControlName
        );
        const profitLoss2Control = this.statementOfProfitOrLossForm.get(
          profitLoss2ControlName
        );

        if (!profitLossControl || !profitLoss2Control) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`🔍 Cross-validation: Control not found for ${label}`, {
          //   profitLossFound: !!profitLossControl,
          //   profitLoss2Found: !!profitLoss2Control,
          // });
          return;
        }

        // Compare the LIVE (recompute-of-latest) form values — the snapshot is
        // never an operand here (revised global rule).
        const profitLossRaw = profitLossControl.value;
        const profitLoss2Raw = profitLoss2Control.value;
        const profitLossValue = Math.round((parseFloat(profitLossRaw) || 0) * 100) / 100;
        const profitLoss2Value = Math.round((parseFloat(profitLoss2Raw) || 0) * 100) / 100;

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`🔍 Cross-validation: Values for ${label}`, {
        //   profitLossValue,
        //   profitLoss2Value,
        //   difference: Math.abs(profitLossValue - profitLoss2Value),
        // });

        // Cross-validation: Check if both calculations match
        if (profitLossValue !== profitLoss2Value) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`❌ Cross-validation: MISMATCH detected for ${label}`);

          // Error for ifrs-full:ProfitLoss (points to ProfitLoss_2)
          profitLossErrors.push(
            this.equalityValidation.formatFieldMismatch({
              columnLabel: label,
              fieldQname: 'Total Profit (loss)',
              fieldSheetKey: SHEET_KEYS.statementOfProfitOrLoss,
              taxonomy,
              sheetLabelOverride: 'Statement of Profit or Loss',
            })
          );

          // Error for ifrs-full:ProfitLoss_2 (points to ProfitLoss)
          profitLoss2Errors.push(
            this.equalityValidation.formatFieldMismatch({
              columnLabel: label,
              fieldQname: 'Profit (loss)',
              fieldSheetKey: SHEET_KEYS.statementOfProfitOrLoss,
              taxonomy,
              sheetLabelOverride: 'Statement of Profit or Loss',
            })
          );
        } else {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`✅ Cross-validation: Values match for ${label}`);
        }
      }
    );

    // REPLACE (not append) this block's ProfitLoss <-> ProfitLoss_2 mismatch
    // messages each cycle.
    //
    // `:ProfitLoss` and `:ProfitLoss_2` declare a same-sheet schema equality, so
    // validateCrossSheet freezes them into crossSheetErrorQnames. validateSameSheet
    // then PRESERVES those keys across every live-edit cycle — which let the old
    // append (`[...existing, ...fresh]`) re-add the same mismatch message on each
    // keystroke, stacking it without bound on repeated mismatch->match->mismatch
    // toggles (mirrors the role18 :End/:End_occurrence2 hotfix). Keep the
    // EXTRACTED_SUM_MARKER suggested-change warnings already on the key, then set the
    // fresh mismatch errors — or clear the key when the two calculations reconcile.
    // The blocking red border is driven by equalityValidationErrors (untouched here),
    // so replacing the message list never drops the verdict.
    //
    // CRUCIAL — `:ProfitLoss` ALSO declares a CROSS-SHEET equality (vs SOCIE
    // "Profit (loss)"). That message lives on the SAME key, sourced by
    // runEqualityValidations and recorded in equalityValidationErrors. The first
    // cc0402a revision preserved only EXTRACTED_SUM_MARKER, so once the same-sheet
    // ProfitLoss <-> ProfitLoss_2 calc reconciled, this live pass dropped the
    // coexisting SOCIE message (red border survived via equalityValidationErrors,
    // but the text vanished after <1s — the regression this fix addresses). So we
    // also re-source the frozen CROSS-SHEET equality messages (compareWith.sheet
    // pointing at another sheet) from their authoritative map and keep them — never
    // letting the same-sheet recheck clobber a cross-sheet message. Mirrors the
    // role19 SOCIE #4 "two equality sources coexist on one qname" fix.
    const setProfitLossMismatch = (qname: string, fresh: string[]): void => {
      const preservedSumWarnings = (this.validationErrors.get(qname) || []).filter(
        (msg) => msg.includes(EXTRACTED_SUM_MARKER)
      );
      const crossSheetMessages = (this.equalityValidationErrors.get(qname) || [])
        .filter((equalityError) => equalityError.compareWith?.sheet && equalityError.compareWith.sheet !== SHEET_KEYS.statementOfProfitOrLoss)
        .map((equalityError) => equalityError.message);
      const next = [...preservedSumWarnings, ...crossSheetMessages, ...fresh];
      if (next.length > 0) {
        this.validationErrors.set(qname, next);
      } else {
        this.validationErrors.delete(qname);
      }
    };
    setProfitLossMismatch(`${prefix}:ProfitLoss`, profitLossErrors);
    setProfitLossMismatch(`${prefix}:ProfitLoss_2`, profitLoss2Errors);

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔍 Cross-validation: Complete. ValidationErrors Map:', {
    //   totalKeys: this.validationErrors.size,
    //   keys: Array.from(this.validationErrors.keys()),
    // });

    // Run cross-sheet validation (MPERS only)
    // Skip if schema is not loaded yet
    if (!this.schema) {
      return;
    }

  }

  /**
   * Run JSON-driven equality validation
   * Uses EqualityValidationService to validate equality rules defined in form JSON
   */
  private runEqualityValidations(): void {
    if (!this.schema) {
      return;
    }

    // Get current taxonomy
    const isMFRS = this.crossRefService.isMFRS();
    const currentTaxonomy: 'MFRS' | 'MPERS' = isMFRS ? 'MFRS' : 'MPERS';

    // Get data from all sheets via state service (for cross-sheet validation)
    const allSheetData = this.stateService.exportAllData();

    // Cross-sheet recompute parity: recompute the referenced sheets'
    // totals (SOCIE, Cash Flows, …) before comparing against them, so the
    // comparison reads fresh (not stale-extracted) operands — matching what the
    // user would see if they opened the referenced sheet. The SOCIE schema is
    // cached by preloadReferencedSchemas (SOCIE is in this sheet's equality
    // rules), so its equityColumns + column visibility are available here.
    const socieSchemaForCtx = this.validationContext.getCachedSchema(
      this.validationContext.resolveSheetKey(SHEET_KEYS.statementOfChangesInEquity) ?? SHEET_KEYS.statementOfChangesInEquity
    );
    const socieColumnContext = buildSocieColumnContext(
      this.crossRefService.isConsolidated(),
      this.crossRefService.is1stTimePreparation(),
      socieSchemaForCtx?.equityColumns
    );
    const referenced = referencedSheetKeysFromSchema(this.schema, SHEET_KEYS.statementOfProfitOrLoss);
    // Parity with the headless role-12 validator: this sheet's Role-13-linked
    // leaves (Revenue, CostOfSales, OtherIncome, OtherExpenseByFunction,
    // FinanceIncome) are readonly `linkTo.role:'310100'` fields whose source of
    // truth is Role 13 (Analysis of P&L). The pure validator applies
    // applyLinkedInheritance + recompute to those leaves BEFORE the
    // ProfitLossBeforeTax equality, so its operand reflects the inherited AoPL
    // values. Recomputing only the *referenced* sheets here left SOPL's own
    // operand at whatever the live form held — which can still be the stale
    // N8N extraction when AoPL disagrees with the persisted leaves — so the
    // mounted equality compared a different ProfitLossBeforeTax than the
    // headless path (green-tick-on-listing → red-cross-on-open, flagged by the
    // parity harness). Include the CURRENT sheet in the recompute set so both
    // paths inherit AoPL and recompute identically.
    const recomputeKeys = [...referenced, SHEET_KEYS.statementOfProfitOrLoss];
    const formValuesWithAllSheets = recomputeReferencedSheets(
      { ...allSheetData, [SHEET_KEYS.statementOfProfitOrLoss]: this.statementOfProfitOrLossForm.getRawValue() },
      recomputeKeys,
      currentTaxonomy,
      // The current sheet's schema is `this.schema` (always loaded by the time
      // validation runs); fall back to the validation-context cache for the
      // referenced sheets. Without the explicit current-sheet branch the
      // generic lookup could miss SOPL's controls and silently skip the AoPL
      // inheritance step for the operand we are validating.
      (key) =>
        key === SHEET_KEYS.statementOfProfitOrLoss
          ? this.schema
          : this.validationContext.getCachedSchema(this.validationContext.resolveSheetKey(key) ?? key),
      socieColumnContext
    );

    // ====================================================================
    // Developer Note — REUSABLE mount-vs-pure parity diagnostic PLAYBOOK
    // (kept commented; uncomment the block below to re-enable for this sheet,
    //  or port it to another sheet's equality method to chase the same bug).
    //
    // Symptom: ParityCheckService warns that the pure validator flagged a field
    // (hasErrors:true) while the mounted editor published hasErrors:false (or
    // vice-versa). To localise the break, trace the chain in THREE layers:
    //
    //   1. OPERANDS (this block) — for the flagged qname, dump, per column:
    //        • the LHS actually compared (post inherit + recompute),
    //        • the LHS pre-recompute (live form), so you can see whether the
    //          recompute/inheritance overlay actually changed it,
    //        • the inheritance SOURCE leaves (here Role-13 AoPL) we expect to
    //          inherit FROM, and whether that slice is even present, and
    //        • the RHS (the referenced sheet's value).
    //      Most divergences are visible here: source slice absent at validate
    //      time, schema controls missing so the inherit step is skipped, or the
    //      RHS unexpectedly recomputed.
    //   2. RESULT — the qnames the equality service flagged (see the
    //      "[equality result]" trace after validateAllEqualityRules below). If
    //      the field is flagged here, detection works and the bug is downstream.
    //   3. ROLLUP — see evaluateSheetStatus(). GOTCHA that bit us here and is
    //      LIKELY TO RECUR on any sheet: computeRowStatus() ranks 'Edited' ABOVE
    //      'Failed', so a row whose recomputed/inherited value deviates from the
    //      last-submission baseline shows 'Edited' and a `badge === 'Failed'`
    //      scan misses its real error — the sheet stays green. The fix folds the
    //      blocking-error maps into the sheet verdict directly (see that method).
    // ====================================================================
    // const prefix = this.crossRefService.isMFRS() ? 'ifrs-full' : 'ifrs-smes';
    // const plbtQname = `${prefix}:ProfitLossBeforeTax`;
    // const aoplSlice = allSheetData[SHEET_KEYS.analysisOfProfitOrLoss];
    // const liveForm = this.statementOfProfitOrLossForm.getRawValue();
    // const recomputedSopl = formValuesWithAllSheets[SHEET_KEYS.statementOfProfitOrLoss];
    // const socfSlice = formValuesWithAllSheets[SHEET_KEYS.statementOfCashFlowsIndirectMethod];
    // const cell = (o: Record<string, any> | undefined, q: string, dim: string): unknown =>
    //   o?.[`${q}[${dim}][CurrentYear]`];
    //
    // (['Consolidated', 'Separate'] as const).forEach((dim) => {
    //   console.log(`[Role12 SOPL][equality operands] ${plbtQname}[${dim}][CurrentYear]`, {
    //     socf_PLBeforeTax: cell(socfSlice, plbtQname, dim),
    //     sopl_recomputed_PLBeforeTax: cell(recomputedSopl, plbtQname, dim),
    //     sopl_liveForm_PLBeforeTax: cell(liveForm, plbtQname, dim),
    //     sopl_recomputed_leaves: {
    //       Revenue: cell(recomputedSopl, `${prefix}:Revenue`, dim),
    //       CostOfSales: cell(recomputedSopl, `${prefix}:CostOfSales`, dim),
    //       OtherIncome: cell(recomputedSopl, `${prefix}:OtherIncome`, dim),
    //       OtherExpenseByFunction: cell(recomputedSopl, `${prefix}:OtherExpenseByFunction`, dim),
    //       FinanceIncome: cell(recomputedSopl, `${prefix}:FinanceIncome`, dim),
    //     },
    //     aopl_source_leaves: {
    //       present: !!aoplSlice && Object.keys(aoplSlice).length > 0,
    //       Revenue: cell(aoplSlice, `${prefix}:Revenue`, dim),
    //       CostOfSales: cell(aoplSlice, `${prefix}:CostOfSales`, dim),
    //       OtherIncome: cell(aoplSlice, `${prefix}:OtherIncome`, dim),
    //       OtherExpenseByFunction: cell(aoplSlice, `${prefix}:OtherExpenseByFunction`, dim),
    //       FinanceIncome: cell(aoplSlice, `${prefix}:FinanceIncome`, dim),
    //     },
    //     sopl_liveForm_leaves: {
    //       Revenue: cell(liveForm, `${prefix}:Revenue`, dim),
    //       OtherIncome: cell(liveForm, `${prefix}:OtherIncome`, dim),
    //       FinanceIncome: cell(liveForm, `${prefix}:FinanceIncome`, dim),
    //     },
    //   });
    // });
    // console.log('[Role12 SOPL][equality operands] context', {
    //   currentTaxonomy,
    //   soplSchemaControls: this.schema?.controls?.length ?? null,
    //   recomputeKeys,
    //   aoplPresent: !!aoplSlice && Object.keys(aoplSlice).length > 0,
    //   socfPresent: !!socfSlice && Object.keys(socfSlice).length > 0,
    // });

    // Run equality validation via service (supports both same-sheet and cross-sheet)
    // Pass SHEET_KEYS.statementOfProfitOrLoss as currentSheetKey so validation knows which sheet we're validating
    const errors = this.equalityValidation.validateAllEqualityRules(
      this.schema,
      formValuesWithAllSheets,
      currentTaxonomy,
      SHEET_KEYS.statementOfProfitOrLoss
    );

    // Diagnostic layer 2 (RESULT) — see playbook in the operand block above.
    // Confirms whether the equality service flagged the field; uncomment with
    // that block (it references `plbtQname` declared there). Re-enable to debug.
    // console.log('[Role12 SOPL][equality result] flagged qnames', {
    //   qnames: Object.keys(errors),
    //   plbtFlagged: Object.prototype.hasOwnProperty.call(errors, plbtQname),
    //   plbtMessages: errors[plbtQname]?.map((e: { message: string }) => e.message),
    // });

    // Clear existing equality errors
    this.equalityValidationErrors.clear();

    // Store new equality errors
    Object.keys(errors).forEach((qname) => {
      this.equalityValidationErrors.set(qname, errors[qname]);

      // Also add to validationErrors Map for display compatibility
      const errorMessages = errors[qname].map((err) => err.message);
      const existingErrors = this.validationErrors.get(qname) || [];
      // Keep only API mismatch warnings (contain EXTRACTED_SUM_MARKER)
      const apiMismatchWarnings = existingErrors.filter((msg) =>
        msg.includes(EXTRACTED_SUM_MARKER)
      );
      this.validationErrors.set(qname, [
        ...apiMismatchWarnings,
        ...errorMessages,
      ]);
    });

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔍 Equality validation complete:', {
    //   totalErrors: this.equalityValidationErrors.size,
    //   fields: Array.from(this.equalityValidationErrors.keys()),
    // });

  }

  /**
   * Validate mandatory fields - checks if required fields have null value
   * 0 is acceptable, only null triggers the error "This field is mandatory."
   * Required fields are defined in the schema JSON with "required: true"
   */
  private validateMandatoryFields(): void {
    // Get mandatory fields from schema (fields with required: true)
    const mandatoryControls =
      this.schema?.controls.filter(
        (control) => control.required === true && !this.isSectionHeader(control)
      ) || [];
    const mandatoryQnames = mandatoryControls.map((control) => control.qname);

    // Define columns to validate based on visibility (using bracket notation)
    const columnsToValidate: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'CurrentYear' | 'PreviousYear';
      label: string;
      show: boolean;
    }> = [
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'CurrentYear',
        label: 'Consolidated Current',
        show: this.showConsolidatedColumns,
      },
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'PreviousYear',
        label: 'Consolidated Previous',
        show: this.showConsolidatedColumns && this.hasPreviousYear,
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'CurrentYear',
        label: 'Separate Current',
        show: true, // Always visible
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'PreviousYear',
        label: 'Separate Previous',
        show: this.hasPreviousYear,
      },
    ];

    mandatoryQnames.forEach((qname) => {
      const errors: string[] = [];

      columnsToValidate.forEach(({ consolidatedOrSeparate, period, label, show }) => {
        if (!show) return;

        // Use bracket notation to match form control names
        const controlName = `${qname}[${consolidatedOrSeparate}][${period}]`;
        const formControl = this.statementOfProfitOrLossForm.get(controlName);

        // Check if value is null (0 is acceptable)
        if (formControl && formControl.value === null) {
          const errorMessage = `${label}: This is a mandatory field.`;
          errors.push(errorMessage);
          this.mandatoryFieldErrors.add(qname);
        }
      });

      if (errors.length > 0) {
        const existingErrors = this.validationErrors.get(qname) || [];
        this.validationErrors.set(qname, [...existingErrors, ...errors]);
      }
    });
  }

  /**
   * Validate conditional mandatory fields based on requiredIf configuration
   * Checks if fields are required based on conditions from other sheets (e.g., Role-0)
   * Example: ProfitLossAttributableToOwnersOfParent is required when NatureOfFinancialStatements is "Consolidated"
   */
  private validateConditionalMandatoryFields(): void {
    // Get controls with requiredIf configuration
    const conditionalControls =
      this.schema?.controls.filter(
        (control) => control.requiredIf && !this.isSectionHeader(control)
      ) || [];

    if (conditionalControls.length === 0) return;

    // Define columns to validate based on visibility (using bracket notation)
    const columnsToValidate: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'CurrentYear' | 'PreviousYear';
      label: string;
      show: boolean;
    }> = [
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'CurrentYear',
        label: 'Consolidated Current',
        show: this.showConsolidatedColumns,
      },
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'PreviousYear',
        label: 'Consolidated Previous',
        show: this.showConsolidatedColumns && this.hasPreviousYear,
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'CurrentYear',
        label: 'Separate Current',
        show: true, // Always visible
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'PreviousYear',
        label: 'Separate Previous',
        show: this.hasPreviousYear,
      },
    ];

    conditionalControls.forEach((control) => {
      const { sheet, field, equals, errorMessage } = control.requiredIf!;

      // Resolve the referenced value through the SAME rule the column-visibility
      // resolver uses (`resolvePrerequisiteValue`: prereq-first, then filingInfo).
      //
      // A bare `getSheetData('filingPrerequisites')` here was silently dead in
      // Phase 2. Since the role0/role1 phase split, role 0 is the editable
      // filing-info slot in Phase 1 ONLY; Phase 2 hydration writes to role 1
      // (`filingInfo`) and leaves the role-0 slot an empty stub. Role 12 is a
      // Phase-2-only sheet, so the lookup always yielded `undefined`, the
      // `!== equals` guard below always short-circuited, and the three
      // "Profit (loss), attributable to ..." rows never raised their message —
      // while the Consolidated columns still rendered, because visibility
      // already had the filingInfo fallback. The headless
      // `role12StatementOfProfitOrLossValidator` was likewise unaffected:
      // `buildSheetValidationContext` aliases the active filing-info under BOTH
      // state keys. Sharing one resolution rule keeps the three from drifting.
      const dependentValue =
        sheet === SHEET_KEYS.filingPrerequisites
          ? resolvePrerequisiteValue(field, {
              filingPrerequisites: this.stateService.getSheetData(
                SHEET_KEYS.filingPrerequisites
              ),
              filingInfo: this.stateService.getSheetData(SHEET_KEYS.filingInfo),
            })
          : this.stateService.getSheetData(sheet as any)?.[field];

      // Check if condition is met
      if (dependentValue !== equals) {
        return; // Condition not met, field is not required
      }

      // Condition is met - validate that field has value
      const errors: string[] = [];
      const qname = control.qname;

      columnsToValidate.forEach(({ consolidatedOrSeparate, period, label, show }) => {
        if (!show) return;

        // Use bracket notation to match form control names
        const controlName = `${qname}[${consolidatedOrSeparate}][${period}]`;
        const formControl = this.statementOfProfitOrLossForm.get(controlName);

        // Check if value is null (0 is acceptable)
        if (formControl && formControl.value === null) {
          errors.push(`${label}: ${errorMessage}`);
          this.mandatoryFieldErrors.add(qname);
        }
      });

      if (errors.length > 0) {
        const existingErrors = this.validationErrors.get(qname) || [];
        this.validationErrors.set(qname, [...existingErrors, ...errors]);
      }
    });
  }

  /**
   * Get validation status for a row (checks all 4 columns).
   * Delegates to MappingStatusService.computeRowStatus for Edited/Validated* resolution;
   * preserves all existing Failed-branch inputs (form invalidity, mandatory, equality errors).
   * Status priority: Failed > Edited > Validated* > Validated
   */
  getRowValidationStatus(
    control: FieldControl
  ): 'Failed' | 'Edited' | 'Validated*' | 'Validated' {
    // Only check monetary items
    if (control.ui.controlType !== 'number') return 'Validated';

    if (this.isRowInEditMode(control)) {
      const frozen = this.frozenRowStatus.get(control.qname);
      if (frozen !== undefined) return frozen;
    }

    const columns: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
    }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current' },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous' },
      { consolidatedOrSeparate: 'Separate', period: 'Current' },
      { consolidatedOrSeparate: 'Separate', period: 'Previous' },
    ];
    const controlNames = columns.map((column) =>
      this.getXbrlControlName(control.qname, column.consolidatedOrSeparate, column.period)
    );

    // Gather current values for the Edited/Validated* deviation check.
    const currentValues: Record<string, unknown> = {};
    for (const controlName of controlNames) {
      const fc = this.statementOfProfitOrLossForm.get(controlName);
      if (fc) {
        currentValues[controlName] = fc.value;
      }
    }

    // hasError / hasSuggestedChangeWarning are computed by the shared
    // _computeRowErrorFlags() so the Mapping Status badge and the row
    // background colour intent can never disagree on what counts as a hard
    // error vs a suggested-change warning.
    const { hasError, hasSuggestedChangeWarning } =
      this._computeRowErrorFlags(control);

    return this.mappingStatus.computeRowStatus({
      controlNames,
      currentValues,
      lastSubmissionValues: this.mappingStatus.getLastSubmissionValues(
        SHEET_KEYS.statementOfProfitOrLoss
      ),
      hasError,
      hasSuggestedChangeWarning,
      forceEdited: false,
      // linkTo rows inherit a read-only value from Role-13 and are deliberately
      // filtered out of this sheet's persisted baseline (filterOutLinkedFields),
      // so their current value can never equal the (absent) last-submission
      // baseline. Mark them inherited so the Edited deviation check is skipped —
      // otherwise every inherited row renders a spurious "Edited".
      inherited: !!control.ui.linkTo,
      // Calculated totals are computed client-side (calculateTotals) and are absent
      // from the persisted baseline, so the deviation check would mark them Edited
      // with zero user edits. They are never user-editable (enterEditMode skips
      // them) — mark them derived to skip the Edited branch.
      derived: this.isCalculatedField(control),
    });
  }

  /**
   * Compute the row's hasError / hasSuggestedChangeWarning flags. This is the
   * single definition of "does the row have a hard error" vs "only a
   * suggested-change warning" — consumed by both the Mapping Status (which
   * layers the Edited/forceEdited override on top via getRowValidationStatus)
   * and the row colour intent (which deliberately ignores edit mode). Keeping
   * it in one place means the red/yellow background can never disagree with the
   * Failed/Validated* badge.
   *
   * Extracted VERBATIM from the previous inline computation in
   * getRowValidationStatus — behaviour preserving. Note: `hasError` already
   * folds in the cross-sheet (equalityValidationErrors) and mandatory
   * (mandatoryFieldErrors) predicates that previously drove the now-removed
   * `row-has-cross-sheet-error` class, so the 'error' colour intent keeps those
   * rows RED.
   */
  private _computeRowErrorFlags(
    control: FieldControl
  ): { hasError: boolean; hasSuggestedChangeWarning: boolean } {
    const columns: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
    }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current' },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous' },
      { consolidatedOrSeparate: 'Separate', period: 'Current' },
      { consolidatedOrSeparate: 'Separate', period: 'Previous' },
    ];
    const controlNames = columns.map((column) =>
      this.getXbrlControlName(control.qname, column.consolidatedOrSeparate, column.period)
    );

    // Only a VISIBLE column may paint the row. A column this permutation does not
    // render can never SHOW a message — every message surface on this sheet
    // filters hidden columns out — so counting one here produces a red row with an
    // empty Error Description. That is the UAT defect of 2026-08-26 (role 18's
    // "Cash and cash equivalents at beginning / end of period" rows, opened on a
    // filing with no previous year): `enable()` makes a hidden, permanently-null
    // required cell invalid, and the reveal on row-open then marks it touched.
    //
    // Developer Note (TODO): `hasValue` below is deliberately still read across
    // ALL FOUR columns. Narrowing it to the visible ones is a separate, correct
    // change — a mandatory row whose only value sits in a hidden column IS
    // unsatisfied — but it can only ADD red, so it must not land until this
    // sheet's required-message builder is confirmed to emit a per-visible-column
    // mandatory line for that case. Otherwise it recreates the very defect above,
    // in the other direction. Role 18 DOES narrow both halves — its
    // `validateRequiredFields()` was made the last writer of the per-column
    // mandatory partition on 2026-08-26, so its red is always accompanied by
    // text; that confirmation is what this sheet still owes.
    const columnVisible: boolean[] = [
      this.showConsolidatedColumns,
      this.showConsolidatedColumns && this.hasPreviousYear,
      true,
      this.hasPreviousYear,
    ];

    let hasInvalid = false;
    let hasValue = false;
    controlNames.forEach((controlName, index) => {
      const fc = this.statementOfProfitOrLossForm.get(controlName);
      if (fc) {
        if (columnVisible[index] && fc.invalid && (fc.dirty || fc.touched)) hasInvalid = true;
        if (fc.value !== null && fc.value !== undefined && fc.value !== '') hasValue = true;
      }
    });

    const isRequired = control.required === true;
    let hasError =
      hasInvalid ||
      (isRequired && !hasValue) ||
      this.equalityValidationErrors.has(control.qname) ||
      this.mandatoryFieldErrors.has(control.qname);
    if (!hasError && this.validationErrors.has(control.qname)) {
      const errors = this.validationErrors.get(control.qname) || [];
      hasError = errors.some((message) => !message.includes('does not match the extracted sum'));
    }

    // Suggested-Change warning: all errors are "does not match the extracted sum".
    // Inherited (linkTo) rows participate too. Revenue / (-) Cost of sales /
    // Other income are read-only mirrors of Role-13's calculated totals; when an
    // inherited value differs from THIS sheet's N8N extracted baseline the row
    // must surface "Validated*" (a per-sheet suggested change), not be silently
    // downgraded. The previous `&& !control.ui.linkTo` guard suppressed the
    // asterisk for these rows which — together with the absent-baseline Edited
    // bug fixed below — made them render "Edited" instead of "Validated*".
    let hasSuggestedChangeWarning = false;
    if (this.validationErrors.has(control.qname)) {
      const allErrors = this.validationErrors.get(control.qname) || [];
      const allAreSuggestedChanges = allErrors.every((message) =>
        message.includes('does not match the extracted sum')
      );
      hasSuggestedChangeWarning = allAreSuggestedChanges;
    }

    return { hasError, hasSuggestedChangeWarning };
  }

  /**
   * Compute the row background colour intent: 'error' (red) when the row has a
   * hard error, 'warning' (yellow) when it only has a suggested-change warning,
   * 'none' otherwise. Red outranks yellow. Unlike the Mapping Status this does
   * NOT consult edit mode (forceEdited), so clicking the edit pencil never
   * repaints the row.
   *
   * Mirrors the getRowValidationStatus guard: non-number controls have no
   * colour intent.
   */
  private _computeRowColorIntent(control: FieldControl): 'error' | 'warning' | 'none' {
    if (control.ui.controlType !== 'number') return 'none';
    const { hasError, hasSuggestedChangeWarning } = this._computeRowErrorFlags(control);
    if (hasError) return 'error';
    if (hasSuggestedChangeWarning) return 'warning';
    return 'none';
  }

  /**
   * Public accessor for the row background colour intent used by the template.
   * Role 12 does not cache row status (getRowValidationStatus computes inline),
   * so this likewise computes on demand — no parallel cache is introduced.
   */
  getRowColorIntent(control: FieldControl): 'error' | 'warning' | 'none' {
    return this._computeRowColorIntent(control);
  }

  /**
   * Get error description for a row (aggregates from all 4 columns)
   * Groups errors by category: "Error" (red) and "* Suggested Change" (black)
   */
  getRowErrorDescription(control: FieldControl): string {
    // Only check monetary items
    if (control.ui.controlType !== 'number') return '';

    const errors: string[] = []; // Red - Error category (cross-sheet, mandatory, form control)
    const suggestedChanges: string[] = []; // Black - * Suggested Change category (API mismatch)

    // Column visibility filter helper
    const isVisibleColumn = (error: string): boolean => {
      if (
        error.includes('Consolidated Current') &&
        !this.showConsolidatedColumns
      )
        return false;
      if (
        error.includes('Consolidated Previous') &&
        (!this.showConsolidatedColumns || !this.hasPreviousYear)
      )
        return false;
      if (error.includes('Separate Previous') && !this.hasPreviousYear)
        return false;
      return true;
    };

    // 1. Process validation errors from validationErrors Map
    if (this.validationErrors.has(control.qname)) {
      const allValidationErrors =
        this.validationErrors.get(control.qname) || [];

      allValidationErrors.forEach((errorMsg) => {
        if (!isVisibleColumn(errorMsg)) return;

        if (errorMsg.includes('does not match the extracted sum')) {
          suggestedChanges.push(errorMsg);
        } else {
          errors.push(errorMsg);
        }
      });
    }

    // 2. Collect form control validation errors -> Error category
    // Control name format must match getXbrlControlName: qname[Consolidated|Separate][CurrentYear|PreviousYear]
    const columns: Array<{
      consolidatedOrSeparate: string;
      period: string;
      label: string;
      show: boolean;
    }> = [
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'CurrentYear',
        label: 'Consolidated Current',
        show: this.showConsolidatedColumns,
      },
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'PreviousYear',
        label: 'Consolidated Previous',
        show: this.showConsolidatedColumns && this.hasPreviousYear,
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'CurrentYear',
        label: 'Separate Current',
        show: true,
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'PreviousYear',
        label: 'Separate Previous',
        show: this.hasPreviousYear,
      },
    ];

    for (const { consolidatedOrSeparate, period, label, show } of columns) {
      if (!show) continue;

      const controlName = `${control.qname}[${consolidatedOrSeparate}][${period}]`;
      const formControl = this.statementOfProfitOrLossForm.get(controlName);

      if (
        formControl &&
        formControl.invalid &&
        (formControl.dirty || formControl.touched)
      ) {
        const errorMessages: string[] = [];

        if (formControl.errors) {
          if (formControl.errors['required'])
            errorMessages.push('Required field');
          if (formControl.errors['min'])
            errorMessages.push(`Min: ${formControl.errors['min'].min}`);
          if (formControl.errors['max'])
            errorMessages.push(`Max: ${formControl.errors['max'].max}`);
        }

        if (errorMessages.length > 0) {
          errors.push(`${label}: ${errorMessages.join(', ')}`);
        }
      }
    }

    // 3. Check for required fields with no value (matching getRowValidationStatus logic)
    // Control name format must match getXbrlControlName: qname[Consolidated|Separate][CurrentYear|PreviousYear]
    const isRequired = control.required === true;
    if (isRequired) {
      const allControlConfigs = [
        { consolidatedOrSeparate: 'Consolidated', period: 'CurrentYear' },
        { consolidatedOrSeparate: 'Consolidated', period: 'PreviousYear' },
        { consolidatedOrSeparate: 'Separate', period: 'CurrentYear' },
        { consolidatedOrSeparate: 'Separate', period: 'PreviousYear' },
      ];
      let hasValue = false;
      for (const { consolidatedOrSeparate, period } of allControlConfigs) {
        const controlName = `${control.qname}[${consolidatedOrSeparate}][${period}]`;
        const formControl = this.statementOfProfitOrLossForm.get(controlName);
        if (
          formControl &&
          formControl.value !== null &&
          formControl.value !== undefined &&
          formControl.value !== ''
        ) {
          hasValue = true;
          break;
        }
      }
    }

    // 4. Format output grouped by category with CSS classes
    // Error section in red (.error-text), Suggested Change section in black (.suggested-change-text)
    let result = '';

    if (errors.length > 0) {
      result += '<span class="error-text">ERROR</span><br>';
      result +=
        '<span class="error-text">' +
        errors.map((errorText, errorIndex) => `${errorIndex + 1}. ${errorText}`).join('<br><br>') +
        '</span>';
    }

    if (suggestedChanges.length > 0) {
      if (result) result += '<br><br>';
      result +=
        '<span class="error-text">*</span><span class="suggested-change-text"> SUGGESTED CHANGE</span><br>';
      result +=
        '<span class="suggested-change-text">' +
        suggestedChanges.map((changeText, changeIndex) => `${changeIndex + 1}. ${changeText}`).join('<br><br>') +
        '</span>';
    }

    // Ctrl+Alt+B baseline debug overlay (non-production). Appended to whatever
    // real validation text this row already has (often none for a clean total).
    // baselineDebugForControl returns '' unless this row is a qualifying total.
    // Columns + getControlName rebuilt inline to mirror validateAgainstApiData's
    // computeMismatchWarnings call exactly (same visible columns + flat keys).
    if (this.baselineDebug.enabled()) {
      const columns: { key: string; label: string }[] = [];
      if (this.showConsolidatedColumns)                         columns.push({ key: 'Consolidated|CurrentYear',  label: 'Consolidated Current' });
      if (this.showConsolidatedColumns && this.hasPreviousYear) columns.push({ key: 'Consolidated|PreviousYear', label: 'Consolidated Previous' });
      columns.push({ key: 'Separate|CurrentYear', label: 'Separate Current' });
      if (this.hasPreviousYear)                                 columns.push({ key: 'Separate|PreviousYear', label: 'Separate Previous' });
      const baselineInfo = this.extractedSumWarning.baselineDebugForControl(
        SHEET_KEYS.statementOfProfitOrLoss,
        control as unknown as SummableControl,
        columns,
        (qname, key) => {
          const [cs, period] = key.split('|');
          return `${qname}[${cs}][${period}]`;
        }
      );
      if (baselineInfo) {
        result = result ? `${result}${baselineInfo}` : baselineInfo;
      }
    }

    return result;
  }

  /**
   * Check if row has Error category items (for red text styling)
   * Error category includes: cross-sheet validation, mandatory field, form control errors
   */
  hasErrorCategory(control: FieldControl): boolean {
    if (this.equalityValidationErrors.has(control.qname)) return true;
    if (this.mandatoryFieldErrors.has(control.qname)) return true;

    const columns: Array<{ suffix: string; show: boolean }> = [
      { suffix: 'ConsolidatedCurrent', show: this.showConsolidatedColumns },
      {
        suffix: 'ConsolidatedPrevious',
        show: this.showConsolidatedColumns && this.hasPreviousYear,
      },
      { suffix: 'SeparateCurrent', show: true },
      { suffix: 'SeparatePrevious', show: this.hasPreviousYear },
    ];

    for (const { suffix, show } of columns) {
      if (!show) continue;

      const controlName = control.qname + suffix;
      const formControl = this.statementOfProfitOrLossForm.get(controlName);

      if (
        formControl &&
        formControl.invalid &&
        (formControl.dirty || formControl.touched)
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if row has Suggested Change category items (for black text styling)
   * Suggested Change category includes: API mismatch warnings
   */
  hasSuggestedChangeCategory(control: FieldControl): boolean {
    if (!this.validationErrors.has(control.qname)) return false;

    const allErrors = this.validationErrors.get(control.qname) || [];

    return allErrors.some((errorMsg) => {
      if (
        errorMsg.includes('Consolidated Current') &&
        !this.showConsolidatedColumns
      )
        return false;
      if (
        errorMsg.includes('Consolidated Previous') &&
        (!this.showConsolidatedColumns || !this.hasPreviousYear)
      )
        return false;
      if (errorMsg.includes('Separate Previous') && !this.hasPreviousYear)
        return false;

      return errorMsg.includes('does not match the extracted sum');
    });
  }

  /**
   * Check if row has failed validation
   */
  hasRowFailed(control: FieldControl): boolean {
    return this.getRowValidationStatus(control) === 'Failed';
  }

  /**
   * Evaluate sheet status by looping through all rows' mapping status.
   * Sets hasErrors = true if any row has 'Failed' status, false otherwise.
   * Called at init and before submit.
   */
  evaluateSheetStatus(): void {
    if (!this.schema?.controls) return;

    let hasErrors = false;
    for (const control of this.schema.controls) {
      if (this.getRowValidationStatus(control) === 'Failed') {
        hasErrors = true;
        break;
      }
    }

    // `computeRowStatus` ranks 'Edited' ABOVE 'Failed': a row whose current
    // value deviates from the last-submission baseline resolves to 'Edited'
    // even when it also carries a real blocking error. The AoPL-recomputed
    // ProfitLossBeforeTax (−19.61) deviates from the extracted baseline (13.62),
    // so its genuine cross-sheet mismatch displays as 'Edited' and the badge
    // scan above never sees 'Failed' — leaving the sheet green while the pure
    // validator (which has no 'Edited' concept) reports an error. Fold the hard
    // blocking-error maps in directly so the SHEET-LEVEL verdict reflects real
    // errors regardless of the per-row display badge. These maps are populated
    // only for genuine blocking mismatches (equality) / missing-required
    // (mandatory), so a non-empty map is always a real, reportable error.
    if (
      !hasErrors &&
      (this.equalityValidationErrors.size > 0 || this.mandatoryFieldErrors.size > 0)
    ) {
      hasErrors = true;
    }

    // Diagnostic layer 3 (ROLLUP) — see the playbook in runEqualityValidations().
    // Confirms the blocking-error maps → published hasErrors and WHEN this runs
    // relative to the pass that populates them. The 'Edited'-masks-'Failed'
    // gotcha this guards against is the most reusable lesson here. Uncomment to
    // re-enable (re-add a `firstFailedQname` capture in the loop if you want the
    // specific field surfaced in the log).
    // console.log('[Role12 SOPL][evaluateSheetStatus] publishing', {
    //   hasErrors,
    //   equalityErrorQnames: Array.from(this.equalityValidationErrors.keys()),
    //   mandatoryErrorQnames: Array.from(this.mandatoryFieldErrors),
    //   isLoadingState: this.isLoadingState,
    //   isValidationComplete: this.isValidationComplete,
    // });

    this.stateService.updateSheetLevelStatus(SHEET_KEYS.statementOfProfitOrLoss, { hasErrors });
  }

  /**
   * Check if row has validation error (for yellow highlighting)
   * Checks validationErrors Map directly instead of status
   */
  hasRowValidationError(control: FieldControl): boolean {
    return this.validationErrors.has(control.qname);
  }

  /**
   * Check if row has equality validation error (for red highlighting)
   * Used to differentiate equality errors from regular validation errors
   */
  hasCrossSheetValidationError(control: FieldControl): boolean {
    return this.equalityValidationErrors.has(control.qname);
  }

  /**
   * Check if row has mandatory field error (for red highlighting)
   * Mandatory field errors occur when required fields have null values
   */
  hasMandatoryFieldError(control: FieldControl): boolean {
    return this.mandatoryFieldErrors.has(control.qname);
  }

  /**
   * Check if a specific input field (column) has a 'Failed' status error
   * Used for applying red border styling to individual input fields
   * Only returns true for mandatory field errors (null values in required fields)
   * @param control The schema control for the row
   * @param suffix The column suffix (e.g., 'ConsolidatedCurrent', 'SeparateCurrent')
   * @returns true if this specific column has a Failed status error
   */
  hasColumnError(control: FieldControl, suffix: string): boolean {
    if (control.ui.controlType !== 'number') return false;

    // Get the form control for this specific column
    const controlName = control.qname + suffix;
    const formControl = this.statementOfProfitOrLossForm.get(controlName);

    if (!formControl) return false;

    // Check if form control is invalid (dirty/touched)
    if (formControl.invalid && (formControl.dirty || formControl.touched)) {
      return true;
    }

    // Check for mandatory field error (null value in required field)
    // Only check if this qname is in mandatoryFieldErrors set
    if (
      this.mandatoryFieldErrors.has(control.qname) &&
      formControl.value === null
    ) {
      return true;
    }

    return false;
  }

  /**
   * Check if a specific field (control + suffix) has an equality validation error
   * Used to apply red border to individual input fields
   */
  hasFieldMismatchError(control: FieldControl, suffix: string): boolean {
    // Only show field-level errors for equality validation errors
    if (!this.equalityValidationErrors.has(control.qname)) {
      return false;
    }

    // Check if the error message mentions this specific column
    const errors = this.validationErrors.get(control.qname) || [];

    // Map suffix to column label for matching error messages
    const columnLabel = suffix
      .replace('Consolidated', 'Consolidated ')
      .replace('Separate', 'Separate ')
      .replace('Current', 'Current')
      .replace('Previous', 'Previous');

    // Check if any actual error (not suggested change) mentions this specific column
    // Suggested changes ("does not match the extracted sum") should not trigger red borders
    return errors.some((error) =>
      error.includes(columnLabel) && !error.includes('does not match the extracted sum')
    );
  }

  onCancelConfirmed(): void {
    // Set flag to skip validation during cancel flow
    this.isCancellingChanges = true;

    // Clear all editing states and the sheet-editing flag (R5b: full-revert while a row is mid-edit)
    this.editingRows.clear();
    this.frozenRowStatus.clear();
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfProfitOrLoss);

    // Clear validation errors
    this.validationErrors.clear();

    // Reload from the reverted state service data (original API values)
    this.isValidationComplete = false;
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.statementOfProfitOrLossForm.markAsPristine();

    // Reset flag after loadSavedState's setTimeout completes (100ms + buffer)
    setTimeout(() => {
      this.isCancellingChanges = false;
    }, 150);
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: FieldControl): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
